// src/components/Chat.tsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Model, BillingSource, UserTeamSummary, ConversationDetail } from '../types';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { chatStyles } from '../styles/chat.styles';
import config from '../config';
import Sidebar from './Sidebar';
import { RightSidebar } from './RightSidebar';
import MainContent from './MainContent';
import VoiceMode from './VoiceMode';
import ShareLinkModal from './ShareLinkModal';
import { getOwnerShare } from './chat/shareService';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { UserIcon, AIIcon } from './Icons';
import ChatInput from './ChatInput';
import ImageLightbox from './chat/ImageLightbox';
import { identifyUser } from '../index';
import { modelCategories, getModelDisplayName as configGetModelDisplayName, getModelIcon as configGetModelIcon, isModelPremium, isModelLite } from '../config/models';
import { useConversations } from './chat/useConversations';
import { storeFile, initializeFileDB } from './chat/fileDatabase';
import { 
  PerplexityChunk,
  MessageWithCitations,
  Conversation,
  ConversationMessage,
  AnalyzeResponse,
  StoredSuggestions
} from './chat/chatTypes';
import { useFileUpload } from './chat/useFileUpload';
import { useTheme } from '../theme/useTheme';
import {
  getPlaceholderText
} from './chat/chatUtils';
import {
  fetchConversationMessages as fetchMessages,
  restoreScrollPosition,
  FetchMessagesParams
} from './chat/conversationService';
import {
  handleKeyPress as messageHandleKeyPress,
  handleTextAreaHeight as messageHandleTextAreaHeight,
  resetTextAreaHeight as messageResetTextAreaHeight,
  handleSubmit as messageHandleSubmit,
  MessageOperationsConfig,
  MessageOperationsCallbacks
} from './chat/messageOperations';
import {
  updateConversationTitle as titleUpdateConversationTitle,
  handleTitleSubmit as titleHandleTitleSubmit,
  startEditing as titleStartEditing,
  handleTitleUpdate as titleHandleTitleUpdate,
  TitleManagementConfig,
  TitleManagementCallbacks
} from './chat/titleManagement';
import {
  handleLogout as authHandleLogout,
  AuthConfig,
  AuthCallbacks
} from './chat/authService';
import {
  useMessageSubmit,
  MessageSubmitDependencies,
  MessageSubmitCallbacks
} from './chat/useMessageSubmit';
import {
  useMessageRenderer,
  MessageRendererDependencies,
  MessageRendererCallbacks
} from './chat/useMessageRenderer';
import {
  getSharedConversations,
  setConversationVisibility as apiSetConversationVisibility,
  listProjects,
  createProject as apiCreateProject,
  setConversationProject as apiSetConversationProject,
  SharedConversation,
  ConversationVisibility,
  ProjectRow,
  ProjectVisibility,
  TeamError,
  TeamUnauthorizedError,
  listPlaybooks,
  startPlaybook,
  completePlaybookRun,
  getMyPrograms,
  PlaybookRow,
  PlaybookRunSummary,
  ProgramRow,
} from './chat/teamService';
import TeamPanel from './Team';
import ProjectPanel from './Project';
import {
  dismissTeamWelcome,
  getPendingTeamWelcome,
  TeamWelcome
} from './chat/teamWelcome';




const BASE_URL = config.apiUrl;

// Add animation styles
const loadingAnimationStyles = `
  @keyframes pulse {
    0% { opacity: 0.4; }
    50% { opacity: 1; }
    100% { opacity: 0.4; }
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes stepFadeIn {
    from { opacity: 0; transform: translateY(-3px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

// One shell, three surfaces (nav rework 2026-07-27). The pathname decides which
// centre column renders; the sidebar and the top bar are mounted for all three.
export type ChatSurface = 'chat' | 'team' | 'project';

const deriveSurface = (pathname: string): ChatSurface => {
  if (pathname === '/team' || pathname.startsWith('/team/')) return 'team';
  if (pathname.startsWith('/p/')) return 'project';
  return 'chat';
};

// /p/123 -> 123. Anything else (or a malformed id) is null and the panel
// bounces back to the chat surface itself.
const deriveProjectId = (pathname: string): number | null => {
  const match = pathname.match(/^\/p\/(\d+)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Which projects are expanded in the sidebar, persisted so the spine looks the
// same after a reload. Bad JSON simply means "nothing expanded".
const EXPANDED_PROJECTS_KEY = 'powerai_project_expanded';

const readExpandedProjects = (): Set<number> => {
  try {
    const raw = localStorage.getItem(EXPANDED_PROJECTS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v: unknown) => typeof v === 'number'));
  } catch {
    return new Set();
  }
};

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const location = useLocation();

  const surface = deriveSurface(location.pathname);
  const isChatSurface = surface === 'chat';
  const panelProjectId = deriveProjectId(location.pathname);

  // Where the top bar's back chevron returns to. Updated on every chat-surface
  // render, so leaving a conversation for /team and coming back lands on that
  // same conversation instead of a generic /c.
  const lastChatPathRef = useRef<string>('/c');
  const currentPath = `${location.pathname}${location.search}`;
  if (isChatSurface) {
    lastChatPathRef.current = currentPath;
  }

  // The chevron POPS the stack instead of always jumping home: /team -> open a
  // project -> back must land on /team, not on the last chat. We remember the
  // surface we came FROM together with the exact path we left it on, and only
  // fall back to the last chat path (then /c) when there is no such surface.
  //
  // Both refs are written during render on purpose (same idiom as
  // lastChatPathRef above) so the label below is correct on the very first
  // render after a surface change instead of lagging by one effect tick. A
  // StrictMode double-render is a no-op: the second pass sees the surface it
  // just recorded and leaves previousSurfaceRef alone.
  type SurfaceEntry = { surface: ChatSurface; path: string };
  const surfaceEntryRef = useRef<SurfaceEntry>({ surface, path: currentPath });
  const previousSurfaceRef = useRef<SurfaceEntry | null>(null);
  if (surfaceEntryRef.current.surface !== surface) {
    previousSurfaceRef.current = surfaceEntryRef.current;
  }
  surfaceEntryRef.current = { surface, path: currentPath };

  // Only a DIFFERENT surface is a valid pop target: standing on /p/2 having
  // come from /p/1 should still go back to whatever preceded the project run.
  const backSurface: ChatSurface =
    previousSurfaceRef.current && previousSurfaceRef.current.surface !== surface
      ? previousSurfaceRef.current.surface
      : 'chat';
  const backLabel =
    location.pathname.startsWith('/team/adoption')
      ? 'Kembali ke Tim'
      : location.pathname.startsWith('/team/people')
        ? 'Kembali ke Tim'
        : location.pathname.startsWith('/team/playbooks')
          ? 'Kembali ke Tim'
          : location.pathname.startsWith('/team/programs')
            ? 'Kembali ke Tim'
            : location.pathname.startsWith('/team/champion')
              ? 'Kembali ke Tim'
            : backSurface === 'team'
        ? 'Kembali ke Tim'
        : backSurface === 'project'
          ? 'Kembali ke project'
          : 'Kembali ke chat';

  const handleBackToChat = useCallback(() => {
    if (
      location.pathname.startsWith('/team/adoption')
      || location.pathname.startsWith('/team/people')
      || location.pathname.startsWith('/team/playbooks')
      || location.pathname.startsWith('/team/programs')
      || location.pathname.startsWith('/team/champion')
    ) {
      navigate('/team');
      return;
    }
    const previous = previousSurfaceRef.current;
    if (previous && previous.surface !== surface && previous.path) {
      navigate(previous.path);
      return;
    }
    navigate(lastChatPathRef.current || '/c');
  }, [navigate, surface, location.pathname]);

  // "Project tidak ditemukan." and friends: /p/:id bounces to /c through
  // router state rather than silently landing the user on a chat with no
  // explanation. Short-lived - the notice is dismissible and the state is
  // stripped from history as soon as it is read.
  //
  // It is also the app's only in-shell message channel: the share / assign to
  // project / create project failure paths report here too (they used to raise
  // a blocking browser dialog, which freezes the tab and reads like a browser
  // error rather than ours). Those pass tone 'error' so a failure does not look
  // like a neutral note.
  type SurfaceNotice = { text: string; tone: 'info' | 'error' };
  const [surfaceNotice, setSurfaceNotice] = useState<SurfaceNotice | null>(null);
  useEffect(() => {
    const notice = (location.state as any)?.notice;
    if (typeof notice !== 'string' || !notice) return;
    setSurfaceNotice({ text: notice, tone: 'info' });
    // Drop it from history so a reload (or a back/forward step) does not
    // resurrect a message about something that already happened.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  useEffect(() => {
    if (!surfaceNotice) return;
    const timer = window.setTimeout(() => setSurfaceNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [surfaceNotice]);

  // Use conversation management hook
  const {
    conversations,
    setConversations,
    isLoadingConversations,
    hasMoreConversations,
    conversationPage,
    setConversationPage,
    fetchConversations,
    loadMoreConversations
  } = useConversations();
  
  // Initialize from localStorage
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? saved === 'true' : window.innerWidth > 768;
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);


  // Save to localStorage when changed
  useEffect(() => {
    localStorage.setItem('sidebarOpen', isSidebarOpen.toString());
  }, [isSidebarOpen]);

  // Narrow-but-not-mobile windows (768-1100px, e.g. a half-screen laptop
  // window): sidebar 280 + File panel 320 as DOCKED columns leave the thread
  // ~100px wide. Below this width the File panel overlays instead of docking.
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 1100);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsNarrow(window.innerWidth < 1100);
    };
    // (see the --app-h effect below for the mobile shell height)
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // MOBILE APP-SHELL HEIGHT (2026-08-04, structural iOS-keyboard fix): bind
  // --app-h to the VISUAL viewport. The wrapper is position:fixed with
  // height var(--app-h), so when the keyboard opens the whole shell shrinks
  // and the static composer footer rises above the keyboard in layout -
  // Safari then has no reason to pan, and the top bar cannot "bablas".
  useEffect(() => {
    const root = document.documentElement;
    if (!isMobile) {
      root.style.removeProperty('--app-h');
      return;
    }
    const vv = window.visualViewport;
    // MODERN vs LEGACY (2026-08-04 round 7, the actual bug): on iOS >= 17.4
    // Safari honours interactive-widget=resizes-content and animates a
    // LAYOUT-viewport resize - the native path that needs NO JS help. During
    // that animation innerHeight and vv.height move at different times, so
    // the old covered=innerHeight-vv.height formula transiently read >60 and
    // CACHED A PHANTOM KEYBOARD HEIGHT; the next focus pre-shrank the shell
    // by that phantom -> the visible jump-up-then-correct. Now: legacy mode
    // (old iOS: innerHeight stays put while vv shrinks) must prove itself
    // with a STABLE covered value before any machinery arms itself, and the
    // cache key is new so poisoned caches are abandoned.
    const KB_KEY = 'powerai_kb_h2';
    const orientKey = () => (window.innerHeight >= window.innerWidth ? 'p' : 'l');
    const readKb = (): number => {
      try {
        const j = JSON.parse(localStorage.getItem(KB_KEY) || '{}');
        const v = j[orientKey()];
        return typeof v === 'number' ? v : 0;
      } catch { return 0; }
    };
    const saveKb = (h: number) => {
      try {
        const j = JSON.parse(localStorage.getItem(KB_KEY) || '{}');
        j[orientKey()] = h;
        localStorage.setItem(KB_KEY, JSON.stringify(j));
      } catch { /* storage off: only the first-focus flash remains */ }
    };
    // covered must hold the SAME value (+-4px) for this many consecutive
    // samples before it counts as a real legacy keyboard, never an animation
    // frame. ~5 frames = 80ms of stability.
    let stableCovered = 0;
    let stableCount = 0;
    let legacy = readKb() > 60; // a valid cache is proof of legacy behavior
    const apply = () => {
      const h = vv ? Math.round(vv.height) : window.innerHeight;
      root.style.setProperty('--app-h', `${h}px`);
      const covered = vv
        ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
        : 0;
      if (covered > 60) {
        if (Math.abs(covered - stableCovered) <= 4) {
          stableCount += 1;
        } else {
          stableCovered = covered;
          stableCount = 1;
        }
        if (stableCount >= 5) {
          legacy = true;
          saveKb(Math.round(stableCovered));
        }
      } else {
        stableCount = 0;
      }
      // The follow-the-pan translate and the document-scroll zeroing are
      // LEGACY-ONLY: on the native resize path they were the ones fighting
      // the animation. Native mode pins --app-y at 0.
      const y = legacy && vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
      root.style.setProperty('--app-y', `${y}px`);
      if (legacy && window.scrollY > 0) window.scrollTo(0, 0);
    };
    // EVENTS ALONE ARE NOT ENOUGH: some iOS reveal paths change the pan with
    // no visualViewport event, leaving the wrong state parked until an
    // unrelated event fires. So around every focus/blur a short rAF loop
    // samples and corrects EVERY FRAME, then stops - no event delivery
    // required, nothing left parked.
    let rafId = 0;
    let rafUntil = 0;
    const tick = () => {
      apply();
      if (performance.now() < rafUntil) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = 0;
      }
    };
    const kick = (ms: number) => {
      rafUntil = Math.max(rafUntil, performance.now() + ms);
      if (!rafId) rafId = requestAnimationFrame(tick);
    };
    // PRE-EMPTIVE shrink (the "never jumps up" piece): focusin runs
    // synchronously, before Safari's async reveal-scroll. Shrinking the shell
    // right here puts the composer above where the keyboard WILL be, so
    // Safari finds the caret already visible and never pans at all. Only the
    // very first focus on a device (no cached height yet) can still flash;
    // every later focus is clean. The vv events then confirm/correct.
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (t.tagName !== 'TEXTAREA' && t.tagName !== 'INPUT') return;
      // Pre-emptive shrink is LEGACY-ONLY: on the native-resize path the
      // browser does this itself, and pre-shrinking with a guessed height
      // was the visible jump. The forced reflow makes the new layout real
      // before Safari's reveal pass runs.
      const kb = readKb();
      if (legacy && kb > 60) {
        root.style.setProperty(
          '--app-h', `${Math.max(200, window.innerHeight - kb)}px`
        );
        void document.body.offsetHeight;
      }
      kick(2000); // cover the whole keyboard animation + any late reveal
    };
    const onFocusOut = () => kick(1200); // keyboard closing settles too
    const onEvent = () => { apply(); kick(300); };
    apply();
    vv?.addEventListener('resize', onEvent);
    vv?.addEventListener('scroll', onEvent);
    window.addEventListener('resize', onEvent);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      vv?.removeEventListener('resize', onEvent);
      vv?.removeEventListener('scroll', onEvent);
      window.removeEventListener('resize', onEvent);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      root.style.removeProperty('--app-h');
      root.style.removeProperty('--app-y');
    };
  }, [isMobile]);

  // Add token check at component level
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
    } else {
      // Set the cookie for cross-domain authentication when user is already logged in
      Cookies.set('powerai_auth', token, {
        expires: 300, // 300 days to match Login.tsx
        domain: '.powerai.id',
        secure: true,
        sameSite: 'lax'
      });
    }
  }, [navigate]);

  const [messages, setMessages] = useState<MessageWithCitations[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  // Multi-select model state (1-3). Single-select is the common case
  // (length === 1 → existing /chat/stream path); 2-3 selected fans out
  // to /api/v1/ai/compare/stream and renders side-by-side cards.
  // Model selection has been removed from the UI: every request now routes
  // through the backend's 'auto' router. We ignore the old localStorage keys
  // and always start (and stay) on 'auto'. Compare mode (length > 1) is
  // therefore unreachable; submitCompare stays dormant.
  const [selectedModels, setSelectedModelsRaw] = useState<Model[]>(['auto']);

  // Derived single-model alias kept for the many existing callsites that
  // expect a single Model. `setSelectedModel(m)` collapses to a single
  // entry in selectedModels, mirroring the legacy single-select setter.
  const selectedModel = selectedModels[0];
  const setSelectedModel = useCallback(
    (m: Model) => setSelectedModelsRaw([m]),
    []
  );
  const setSelectedModels = setSelectedModelsRaw;
  const [isLoading, setIsLoading] = useState(false);
  // Inline composer error for workspace upload failures (403/413/network).
  // Shown in the composer area; cleared at the start of each send and on
  // new-chat / conversation-switch.
  const [uploadError, setUploadError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<{ file: File; filename: string }[]>([]);
  
  const [shouldScroll, setShouldScroll] = useState(true);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);

  // Live mirror of currentConversationId for the stream consumer's view gate
  // (a run for conversation A must never paint into conversation B / New chat).
  // Render-synced below + eagerly updated at every navigation site so async
  // consumers always see the truth without waiting for a re-render.
  const currentConversationIdRef = useRef<number | null>(null);
  currentConversationIdRef.current = currentConversationId;
  const [isInitialized, setIsInitialized] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  // Keep backward compatibility with single file support
  const selectedFile = selectedFiles.length > 0 ? selectedFiles[0].file : null;
  const selectedFileName = selectedFiles.length > 0 ? selectedFiles[0].filename : null;
  const setSelectedFile = (file: File | null) => {
    if (file === null) {
      setSelectedFiles([]);
    }
  };
  const setSelectedFileName = (filename: string | null) => {
    // This is for backward compatibility but won't be used in practice
  };

  // Branchout state
  const [branchingMessageId, setBranchingMessageId] = useState<number | null>(null);

  // Branchout handler
  const handleBranchout = async (message: MessageWithCitations) => {
    // Branching is a write (owner-only server side). Shared views are
    // read-only, so this is a no-op there and the button never latches.
    if (readOnly) return;
    if (!currentConversationId || !message.id || message.fromServer !== true) return;

    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    const conversationUuid =
      conversations.find(c => c.id === currentConversationId)?.uuid || uuid;
    if (!conversationUuid) return;

    setBranchingMessageId(message.id);
    try {
      const response = await fetch(
        `${BASE_URL}/conversations/uuid/${conversationUuid}/branch`,
        {
          method: 'POST',
          headers: {
            'Authorization': `${tokenType} ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ message_id: message.id })
        }
      );
      if (!response.ok) throw new Error('branch failed');
      const data = await response.json();
      if (data.uuid) {
        fetchConversations(1);
        navigate(`/c/${data.uuid}`);
      }
    } catch (error) {
      alert('Gagal membuat cabang. Coba lagi.');
    } finally {
      setBranchingMessageId(null);
    }
  };

  // Mode Suara: sesi live voice terikat ke satu conversation. Kalau dibuka
  // dari layar kosong, buat conversation dulu supaya transkrip + komik punya
  // rumah (relay menolak tanpa conversation_id).
  const [voiceConv, setVoiceConv] = useState<{ id: number; uuid: string } | null>(null);
  const openVoiceMode = async () => {
    if (readOnly) return;
    if (currentConversationId) {
      const conv = conversations.find(c => c.id === currentConversationId);
      setVoiceConv({ id: currentConversationId, uuid: conv?.uuid || '' });
      return;
    }
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    try {
      const response = await fetch(`${BASE_URL}/conversations/`, {
        method: 'POST',
        headers: {
          'Authorization': `${tokenType} ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: 'Obrolan suara' })
      });
      if (!response.ok) throw new Error('create failed');
      const data = await response.json();
      setVoiceConv({ id: data.id, uuid: data.uuid });
      setCurrentConversationId(data.id);
      navigate(`/c/${data.uuid}`);
      fetchConversations(1);
    } catch {
      setUploadError('Gagal memulai Mode Suara. Coba lagi ya.');
    }
  };
  const refetchVoiceThread = async (conv: { id: number; uuid: string } | null) => {
    if (!conv) return;
    try {
      await fetchConversationMessages(conv.id, conv.uuid, null, 'next', 50, true);
    } catch { /* thread refetch is best-effort */ }
  };
  const revealVoiceComic = (
    conv: { id: number; uuid: string } | null,
    comic: { url: string; title: string; messageId?: number }
  ) => {
    if (!conv || !comic.url) return;
    // The backend has already committed this message before emitting "done".
    // Put the card in the thread immediately anyway, so visibility never
    // depends on fetch timing, React's in-flight-fetch guard, or closing the
    // overlay. The canonical refetch below replaces this optimistic copy.
    setMessages(prev => {
      if (prev.some(m => m.content.includes(comic.url))) return prev;
      return [...prev, {
        id: comic.messageId ?? -Date.now(),
        role: 'assistant',
        content: `[${comic.title}](${comic.url})`,
        model: 'auto',
        isComplete: true,
        fromServer: comic.messageId != null,
        created_at: new Date().toISOString(),
      } as MessageWithCitations];
    });
    void refetchVoiceThread(conv);
  };
  const closeVoiceMode = async () => {
    const conv = voiceConv;
    setVoiceConv(null);
    // Transkrip + komik dipersist server-side selama sesi; tarik ulang thread
    // supaya langsung kelihatan tanpa refresh manual. Refetch KEDUA menyusul:
    // transkrip giliran terakhir baru di-flush server-side sesaat SETELAH
    // socket ditutup (finalize), jadi tarikan pertama bisa kekurangan 1-2
    // pesan terakhir.
    await refetchVoiceThread(conv);
    setTimeout(() => { refetchVoiceThread(conv); }, 2000);
  };

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);





  // Add this useEffect to handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (modelSwitchRef.current && !modelSwitchRef.current.contains(event.target as Node)) {
        setOpenModelSwitchId(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  // Add state for popup visibility
  const [isModelSwitchOpen, setIsModelSwitchOpen] = useState(false);

  // Add ref for the model switch popup
  const modelSwitchRef = useRef<HTMLDivElement>(null);

  // Add this state to track which message's popup is open
  const [openModelSwitchId, setOpenModelSwitchId] = useState<string | null>(null);


  // Update useEffect to handle clicks outside both dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (modelSwitchRef.current && !modelSwitchRef.current.contains(event.target as Node)) {
        setOpenModelSwitchId(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add this state to track popup position and visibility
  const [popupPosition, setPopupPosition] = useState<{ top: number, left: number } | null>(null);

  // Add paginationData state here
  const [paginationData, setPaginationData] = useState({
    nextCursor: null as string | null,
    previousCursor: null as string | null,
    hasNext: false,
    hasPrevious: false
  });

  // Add ref for textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Add near other state declarations at the top of the Chat component
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  


  // Add this state near your other state declarations
  const [searchQuery, setSearchQuery] = useState('');

  // Add the filtered conversations memo
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    
    return conversations.filter(conversation => 
      conversation.title?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [conversations, searchQuery]);


  // NOTE: `showRightPanel` is derived just below the `readOnly` state (it also
  // depends on it) rather than here, where it used to live.






  // Add effect to focus textarea when conversation changes or on mount
  useEffect(() => {
    if (textareaRef.current && isInitialized) {
      textareaRef.current.focus();
    }
  }, [currentConversationId, isInitialized]);

  // Add effect to focus textarea when model changes on empty screen
  useEffect(() => {
    if (textareaRef.current && messages.length === 0) {
      textareaRef.current.focus();
    }
  }, [selectedModel, messages.length]);

  // NOTE: chat init (conversation fetch + message load + active-run reattach)
  // lives in the single initializeChat useEffect further down. There used to
  // be an identical duplicate effect here; the two racing init sequences
  // (each doing setMessages replacements) could wipe the reattach placeholder
  // appended by whichever sequence finished first - so the duplicate was
  // removed. Keep it single.

  const CONVERSATIONS_PER_PAGE = 20;

  // Add these state declarations at the top of your component
  const [userInfo, setUserInfo] = useState<{
    name: string;
    email: string;
    status: string;
    credits_used: number;
    subscription_type?: string;
    // Team-aware credit fields from /auth/credit-info (see src/types.ts User).
    credits_total?: number | null;
    billing_source?: BillingSource;
    team?: UserTeamSummary | null;
    raw_status?: string;
    has_team?: boolean;
  } | null>(null);

  // Voice Mode is invite-only for a small set of users; everyone else gets the
  // composer without the "Obrolan" (live voice) entry point.
  const VOICE_ALLOWED_EMAILS = ['evanpurnama5@gmail.com', 'aderiesta@gmail.com'];
  const voiceAllowed = !!userInfo && VOICE_ALLOWED_EMAILS.includes((userInfo.email || '').trim().toLowerCase());

  // ---------------------------------------------------------------------------
  // Teams phase 2 - shared conversations
  //
  // `readOnly` is the single gate for "this conversation belongs to another
  // member": it hides every write affordance (composer, submit, share toggle)
  // while leaving the READ paths (messages, active-run reattach, live stream)
  // completely untouched, so watching someone else's run live still works.
  // All of it degrades to today's behaviour against a backend that has not
  // shipped the fields yet: is_owner absent => owner => readOnly false.
  // ---------------------------------------------------------------------------
  const [sharedConversations, setSharedConversations] = useState<SharedConversation[]>([]);
  const [readOnly, setReadOnly] = useState(false);
  // Title straight from the conversation-detail payload. The top-bar crumb used
  // to read it out of `conversations` (the OWN-conversations list), so a shared
  // conversation, which never appears there, always showed "Percakapan baru".
  const [conversationTitle, setConversationTitle] = useState<string | null>(null);
  const [branchedFrom, setBranchedFrom] = useState<{ uuid: string; title: string | null } | null>(null);
  const [shareVisibility, setShareVisibility] = useState<ConversationVisibility>('private');
  const [sharedOwnerEmail, setSharedOwnerEmail] = useState<string | null>(null);
  const [sharePending, setSharePending] = useState(false);
  const [linkShareOpen, setLinkShareOpen] = useState(false);
  const [linkShareActive, setLinkShareActive] = useState(false);
  // False from the moment a conversation is opened until its detail answers.
  // Until then `shareVisibility` is only a DEFAULT, so the share pill would
  // happily show "Bagikan ke tim" on an already-shared conversation and the
  // click would flip it the wrong way. The pill reads this to disable itself
  // ("Memuat…") instead of acting on a guess. A failed detail fetch still
  // marks it loaded: the owner defaults are then the honest state, and the
  // PATCH round-trip is authoritative anyway.
  const [conversationMetaLoaded, setConversationMetaLoaded] = useState(true);

  // The right file panel is a permanent fixed column on desktop and hidden
  // entirely on mobile (no toggle). Derived, not stateful. It is also an
  // owner-only surface: the /files endpoints answer 403 for someone else's
  // conversation, so a read-only view gets no panel and no reserved column.
  // The team/project surfaces have no conversation at all, so it is hidden
  // there too (blueprint 3.1) and the panel gets the full centre column.
  const showRightPanel = !isMobile && !readOnly && isChatSurface;

  // ---------------------------------------------------------------------------
  // Teams phase 3 - projects
  //
  // `projects` feeds both the sidebar sub-list and the top-bar project menu;
  // `conversationProjectId` is the open conversation's assignment (from the
  // detail endpoint, absent => none). `pendingProjectId` is the "new chat in
  // this project" hand-off carried in router state from /p/:id - it rides on
  // the conversation-create call of the FIRST message and is cleared right
  // after. Everything degrades to zero projects against a backend without the
  // routes.
  // ---------------------------------------------------------------------------
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [conversationProjectId, setConversationProjectId] = useState<number | null>(null);
  const [playbookRun, setPlaybookRun] = useState<PlaybookRunSummary | null>(null);
  const [playbooks, setPlaybooks] = useState<PlaybookRow[]>([]);
  const [myPrograms, setMyPrograms] = useState<ProgramRow[]>([]);
  const [playbookCompletePending, setPlaybookCompletePending] = useState(false);
  // Both team listings have answered at least once. Only used to hold back the
  // sidebar's "Belum ada chat tim" hint: membership is known (from credit-info)
  // before those two fetches land, so without this the hint flashes for a
  // moment on every load of a team that does in fact have rows.
  const [sharedLoaded, setSharedLoaded] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  // One-time "welcome to the team" card, armed by /login right after an invite
  // join and read once here (the empty-chat hero is where a new member lands).
  // Read at mount only: nothing else in this session can arm it.
  const [teamWelcome, setTeamWelcome] = useState<TeamWelcome | null>(
    () => getPendingTeamWelcome()
  );
  const handleDismissTeamWelcome = useCallback(() => {
    setTeamWelcome((prev) => {
      if (prev) dismissTeamWelcome(prev.teamId);
      return null;
    });
  }, []);

  const handleStartPlaybook = useCallback(async (row: PlaybookRow) => {
    const schema = row.published_version?.input_schema || row.version?.input_schema || [];
    if (schema.some((field) => field.required)) {
      navigate(`/team/playbooks/${row.uuid}`);
      return;
    }
    try {
      const started = await startPlaybook(row.uuid);
      navigate(`/c/${started.conversation_uuid}`);
    } catch (e) {
      if (e instanceof TeamUnauthorizedError) {
        navigate('/login');
      }
    }
  }, [navigate]);

  const handleCompletePlaybook = useCallback(async () => {
    if (!playbookRun || playbookCompletePending) return;
    setPlaybookCompletePending(true);
    try {
      const done = await completePlaybookRun(playbookRun.id, { useful: true });
      setPlaybookRun({ ...playbookRun, status: done.status });
    } catch (e) {
      if (e instanceof TeamUnauthorizedError) {
        navigate('/login');
      }
    } finally {
      setPlaybookCompletePending(false);
    }
  }, [playbookRun, playbookCompletePending, navigate]);
  const [projectPending, setProjectPending] = useState(false);
  // "New chat inside project X". Since the nav rework the context rides in the
  // URL (/c?project=5) so a reload keeps it; the legacy router-state hand-off is
  // still read for any old client link that carries it.
  const readPendingProject = useCallback((): number | null => {
    const fromQuery = new URLSearchParams(location.search).get('project');
    if (fromQuery) {
      const parsed = Number(fromQuery);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    const pid = (location.state as any)?.projectId;
    return typeof pid === 'number' ? pid : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state]);
  const [pendingProjectId, setPendingProjectId] = useState<number | null>(() =>
    readPendingProject()
  );

  // Re-read the hand-off on every router navigation. A first-message send only
  // does window.history.pushState (not a router navigate), so the pending value
  // survives long enough to reach the create call.
  useEffect(() => {
    setPendingProjectId(readPendingProject());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.search]);

  // The welcome screen's Project pill is removable: drop the context and strip
  // the query param so a reload does not bring it back.
  const handleClearPendingProject = useCallback(() => {
    setPendingProjectId(null);
    if (new URLSearchParams(location.search).has('project')) {
      navigate('/c', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate]);

  // ---------------------------------------------------------------------------
  // Sidebar PROJECT section: which rows are expanded in place (blueprint 3.2).
  // Persisted, plus the project holding the open chat is always opened so the
  // active row is never hidden inside a collapsed parent.
  // ---------------------------------------------------------------------------
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(() =>
    readExpandedProjects()
  );
  useEffect(() => {
    try {
      localStorage.setItem(
        EXPANDED_PROJECTS_KEY,
        JSON.stringify(Array.from(expandedProjects))
      );
    } catch {
      /* storage full or disabled - expansion just stops persisting */
    }
  }, [expandedProjects]);
  const handleToggleProject = useCallback((projectId: number) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);
  const expandProject = useCallback((projectId: number) => {
    setExpandedProjects((prev) => {
      if (prev.has(projectId)) return prev;
      const next = new Set(prev);
      next.add(projectId);
      return next;
    });
  }, []);

  // The project holding the open chat (or the one whose panel is open) starts
  // expanded: a collapsed parent must never hide the row you are standing on.
  useEffect(() => {
    if (conversationProjectId != null) expandProject(conversationProjectId);
  }, [conversationProjectId, expandProject]);
  useEffect(() => {
    if (panelProjectId != null) expandProject(panelProjectId);
  }, [panelProjectId, expandProject]);

  // File upload hook
  const {
    isDragging,
    isFileUploadEnabled,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleClipboardPaste
  } = useFileUpload({
    selectedModel,
    currentConversationId,
    userInfo,
    selectedFiles,
    onFileSelected: (file: File, filename: string) => {
      // For backward compatibility with single file operations like clipboard paste
      setSelectedFiles([{ file, filename }]);
    },
    onFilesSelected: (files: { file: File; filename: string }[]) => {
      // For replacing all files (used for clipboard paste or when needed)
      setSelectedFiles(files);
    },
    onFilesAdded: (files: { file: File; filename: string }[]) => {
      // For adding files to existing selection (used for drag and drop and file input)
      setSelectedFiles(prevFiles => {
        // Prevent duplicate files based on filename
        const existingFilenames = new Set(prevFiles.map(f => f.file.name));
        const newFiles = files.filter(f => !existingFilenames.has(f.file.name));
        return [...prevFiles, ...newFiles];
      });
    }
  });

  // Docked = the File panel reserves layout width; overlaid (narrow windows,
  // <1100px) = it floats above the thread and reserves nothing.
  const panelDocked = showRightPanel && !isNarrow;

  // Theme hook
  const {
    theme,
    setTheme,
    themeColors,
    currentThemeColors,
    updatedLayoutStyles,
    messageStyles,
    updatedInputStyles
  } = useTheme(isMobile, panelDocked);

  // Update the useEffect to fetch the credit info and identify the user in
  // Mixpanel. Kept in a ref as well so the team panel can ask for a refresh
  // after anything that moves credits or membership.
  const fetchCreditInfoRef = useRef<() => Promise<void>>();
  useEffect(() => {
    const fetchCreditInfo = async () => {
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType');

      try {
        const response = await fetch(`${BASE_URL}/auth/credit-info`, {
          headers: {
            'Authorization': `${tokenType} ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch credit info');
        }

        const data = await response.json();
        setUserInfo(data);
        
        // Identify user in Mixpanel when we get their info
        if (data.email) {
          identifyUser(data.email, {
            name: data.name,
            email: data.email,
            status: data.status,
            credits_used: data.credits_used,
            $name: data.name,
            $email: data.email
          });
          
          // Set user properties that we want to track over time
          mixpanel.people.set({
            'Account Status': data.status,
            'Credits Used': data.credits_used
          });
          
          // Increment total logins count
          mixpanel.people.increment('Total Logins');
        }
      } catch (error) {
        console.error('Error fetching credit info:', error);
      }
    };

    fetchCreditInfoRef.current = fetchCreditInfo;
    fetchCreditInfo();
  }, []);

  // One handler for everything the team/project panels can change (membership,
  // pool, projects): re-read the numbers and both sidebar lists. Stable, so the
  // MainContent memo comparator never has to know about it.
  const handlePanelDataChanged = useCallback(() => {
    void fetchCreditInfoRef.current?.();
    void refreshProjectsRef.current?.();
    void refreshSharedConversationsRef.current?.();
  }, []);

  const handleCreditRedeemed = useCallback(async () => {
    await fetchCreditInfoRef.current?.();
  }, []);




  // 1) Add a new state to track if we should show the scroll-to-bottom button
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // 2) Update the handleScroll function to set showScrollToBottom
  const handleScroll = useCallback(() => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isAtBottom = distanceFromBottom < 10;
      setShowScrollToBottom(!isAtBottom);
    }
  }, []);

  // Add both scroll listener and messages effect
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      // Initial check
      handleScroll();
      
      // Add scroll listener
      container.addEventListener('scroll', handleScroll);
      
      // Cleanup
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, messages]); // Add messages as dependency

  // Update the useEffect for auto-scrolling
  useEffect(() => {
    if (messagesEndRef.current && shouldScroll) {
      const isInitialLoad = !paginationData.previousCursor && !paginationData.nextCursor;
      // Only scroll on initial conversation load
      if (isInitialLoad && shouldScroll) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages, shouldScroll, paginationData]);

  // Update the useEffect for processing citations
  useEffect(() => {
    // Model selection has been removed from the UI: selection is pinned to
    // 'auto' for every conversation, so we no longer restore a model picker
    // from message history when loading old chats.


    // Process all unprocessed messages
    const needsProcessing = messages.some(msg => 
      msg.role === 'assistant' && 
      (isPerplexityModel(msg.model) || isClaudeModel(msg.model)) && // Updated condition
      !msg.citations && 
      msg.sources
    );

    if (needsProcessing) {
      setMessages(prev => prev.map(msg => {
        if (msg.role === 'assistant' && 
            (isPerplexityModel(msg.model) || isClaudeModel(msg.model)) && // Updated condition
            !msg.citations && 
            msg.sources) {
          return { ...msg, citations: new Set(msg.sources) };
        }
        return msg;
      }));
    }
  }, [messages]); // Keep messages as dependency

  const processPerplexityText = (text: string, citations?: Set<string>): JSX.Element => {
    // Tambahkan deteksi code block
    const codeBlockRegex = /```([\s\S]*?)```/g;
    const parts = text.split(codeBlockRegex);
    
    return (
      <>
        {parts.map((part, index) => {
          if (index % 2 === 1) { // Ini adalah code block
            const [language, ...codeLines] = part.trim().split('\n');
            const code = codeLines.join('\n');
            
            return (
              <div key={index} style={chatStyles.codeBlock}>
                {language && (
                  <div style={chatStyles.codeLanguage}>
                    {language}
                  </div>
                )}
                <pre style={chatStyles.pre}>
                  <code>{code}</code>
                </pre>
              </div>
            );
          }
          
          // Proses teks normal seperti sebelumnya
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  const processPerplexityCitations = (text: string): Set<string> => {
    const allSources = new Set<string>();
    try {
      // Split into JSON objects and process each one
      const jsonObjects = text.split(/(?=\{'text':)/);
      
      jsonObjects.forEach(obj => {
        try {
          const cleanObj = obj
            .replace(/'/g, '"')
            .replace(/\n/g, '')
            .trim();
            
          if (!cleanObj) return;
          
          const parsed = JSON.parse(cleanObj);
          if (parsed.sources) {
            parsed.sources.forEach((source: string) => allSources.add(source));
          }
        } catch (e) {
          // Ignore parsing errors for individual objects
        }
      });
    } catch (e) {
      console.error('Error processing citations:', e);
    }
    return allSources;
  };

  // Update the key press handler for multiline support
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    messageHandleKeyPress(e, isMobile, handleSubmit);
  };

  // Update the handleTextAreaHeight function
  const handleTextAreaHeight = messageHandleTextAreaHeight;

  // Add this to reset textarea height when message is sent
  const resetTextAreaHeight = messageResetTextAreaHeight;

  // Add this state near your other state declarations
  const [isFirstMessageLoading, setIsFirstMessageLoading] = useState(false);

  // Add an AbortController ref
  const abortControllerRef = useRef<AbortController | null>(null);

  let navigationData: {id: number, uuid: string} | null = null;




  // Add this helper function to get a friendly model name
  const getModelDisplayName = (model: string): string => {
    return configGetModelDisplayName(model);
  };

  // Configuration for message operations service
  const messageOperationsConfig: MessageOperationsConfig = {
    isMobile,
    currentConversationId,
    selectedFile,
    selectedFileName,
    userInfo,
    navigationData: undefined
  };

  const messageOperationsCallbacks: MessageOperationsCallbacks = {
    setCurrentConversationId,
    setConversations,
    setMessages,
    setInputMessage,
    setIsLoading,
    setSelectedFile,
    setSelectedFileName,
    setShouldScroll,
    setIsFirstMessageLoading,
    navigate,
    getModelDisplayName
  };

  // Configuration for title management service
  const titleManagementConfig: TitleManagementConfig = {
    editingTitle,
    editingTitleId
  };

  const titleManagementCallbacks: TitleManagementCallbacks = {
    setConversations,
    setEditingTitleId,
    setEditingTitle
  };

  // Configuration for auth service
  const authConfig: AuthConfig = {
    userInfo
  };

  const authCallbacks: AuthCallbacks = {
    navigate
  };

  // Configuration for message submit hook
  const messageSubmitDependencies: MessageSubmitDependencies = {
    selectedModel,
    inputMessage,
    isLoading,
    selectedFile,
    selectedFileName,
    selectedFiles,
    messages,
    currentConversationId,
    conversations,
    userInfo,
    pendingProjectId,
    abortControllerRef,
    textareaRef,
    chatContainerRef,
    messagesEndRef,
    currentConversationIdRef,
    getModelDisplayName,
    resetTextAreaHeight,
    navigate,
    fetchConversationMessages: fetchMessages,
    // Teams phase 2: refresh the "Tim" sidebar block at the SAME run lifecycle
    // points as the personal conversations list (run start, run done). Without
    // it the shared list only moved on the 12s poll, which is itself gated on
    // the personal list, so a mid-run snapshot could keep a row spinning
    // forever. Reads through the ref so it always runs the latest closure
    // (same pattern as pollConversationsRef); failures stay silent inside it.
    refreshSharedConversations: () => { void refreshSharedConversationsRef.current?.(); },
    // Same lifecycle beat for the personal list: its rows carry the relative
    // time and the server-generated title, neither of which the optimistic
    // stamps can produce.
    refreshConversations: () => { void pollConversationsRef.current?.(); },
    // A finished turn is the moment a brand-new conversation first HAS meta
    // (visibility, project, title). Re-reading the detail here is what makes
    // the share pill and the project pill correct without a reload.
    onConversationMetaChanged: (conversationId: number, conversationUuid: string) => {
      void loadConversationMetaRef.current?.(conversationId, conversationUuid);
    }
  };

  const messageSubmitCallbacks: MessageSubmitCallbacks = {
    setSelectedModel,
    setInputMessage,
    setIsLoading,
    setSelectedFile,
    setSelectedFileName,
    setSelectedFiles,
    setMessages,
    setCurrentConversationId,
    setConversations,
    setUserInfo,
    setIsFirstMessageLoading,
    setShouldScroll,
    setShowScrollToBottom,
    setUploadError,
    clearPendingProject: () => setPendingProjectId(null),
    // A conversation created by the first message has meta from the moment it
    // exists: adopt it now so the pills are never a guess, and treat it as
    // loaded (the run-done hydration re-reads it from the server anyway).
    onConversationCreated: (created) => {
      setConversationProjectId(
        typeof created.project_id === 'number' ? created.project_id : null
      );
      setShareVisibility(created.visibility === 'team' ? 'team' : 'private');
      setConversationMetaLoaded(true);
    }
  };

  // Use the message submit hook
  const { handleSubmit: handleSubmitSingle, consumeRunStream } = useMessageSubmit(messageSubmitDependencies, messageSubmitCallbacks);

  // Keep the latest consumeRunStream in a ref so effects/handlers (which may
  // capture a stale render) always attach through the current closure.
  const consumeRunStreamRef = useRef(consumeRunStream);
  consumeRunStreamRef.current = consumeRunStream;

  // Double-attach guard: run_id -> placeholder message id, set once we've
  // appended a placeholder and started consuming, so a re-fired effect (or
  // StrictMode double-mount) won't attach the same run twice in this tab.
  // Keeping the placeholder id lets a second reattach call RE-ENSURE the
  // placeholder exists (a racing refetch may have replaced messages and wiped
  // it) instead of silently skipping.
  const attachedRunsRef = useRef<Map<string, number>>(new Map());

  // ---------------------------------------------------------------------------
  // Sidebar run-status tracking: which conversations are processing vs freshly
  // finished-with-unseen-results.
  //
  // - `unseenDoneIds`: conversations whose run finished while NOT on screen,
  //   drives the jade "Selesai · hasil baru" badge. Cleared when opened.
  // - `seenDoneRunsRef`: completions this client already showed the user
  //   (watched live, or opened after finish). Stops the server's 90s
  //   `just_done` window from re-badging them on every poll.
  // - `prevRunStatusRef`: last observed status per conversation, so a poll can
  //   detect the running -> finished transition for background runs.
  // ---------------------------------------------------------------------------
  const [unseenDoneIds, setUnseenDoneIds] = useState<Set<number>>(new Set());
  const seenDoneRunsRef = useRef<Set<number>>(new Set());
  const prevRunStatusRef = useRef<Map<number, string>>(new Map());

  // Detect run-status transitions on EVERY conversations change (initial
  // fetch, poll merge, optimistic stamps).
  useEffect(() => {
    const openId = currentConversationIdRef.current;
    const newlyDone: number[] = [];
    for (const cv of conversations) {
      const prev = prevRunStatusRef.current.get(cv.id);
      const cur = cv.active_run_status ?? null;
      if (cur === 'running') {
        // A (new) live run resets any "already seen" latch for this conversation.
        seenDoneRunsRef.current.delete(cv.id);
      } else if (prev === 'running' || cur === 'just_done') {
        // Finished: either we observed it running earlier, or the server says
        // it wrapped up within the last 90s.
        if (cv.id === openId) {
          seenDoneRunsRef.current.add(cv.id); // result is on screen - seen
        } else if (!seenDoneRunsRef.current.has(cv.id) && !unseenDoneIds.has(cv.id)) {
          newlyDone.push(cv.id);
        }
      }
      prevRunStatusRef.current.set(cv.id, cur ?? 'none');
    }
    if (newlyDone.length > 0) {
      setUnseenDoneIds(prev => {
        const next = new Set(prev);
        newlyDone.forEach(id => next.add(id));
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations]);

  // Opening a conversation consumes its "unseen result" badge.
  useEffect(() => {
    if (currentConversationId == null) return;
    const cv = conversations.find(c => c.id === currentConversationId);
    if (cv?.active_run_status !== 'running') {
      seenDoneRunsRef.current.add(currentConversationId);
    }
    setUnseenDoneIds(prev => {
      if (!prev.has(currentConversationId)) return prev;
      const next = new Set(prev);
      next.delete(currentConversationId);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  // Silent refresh of the "Tim" sidebar block. A 404 (no_team, or a backend
  // that has not shipped the endpoint) is expected and simply hides the
  // section - never surfaced to the user. Kept in a ref so every caller runs
  // the latest closure, like the conversations poll below.
  const refreshSharedConversationsRef = useRef<() => Promise<void>>();
  refreshSharedConversationsRef.current = async () => {
    if (!localStorage.getItem('accessToken')) return;
    // Skip the request entirely once the server has told us there is no team.
    // NOTE: /auth/credit-info answers with `team` (null when there is none);
    // there is no `has_team` field on that payload, so gating on it silently
    // disabled every team affordance in the app.
    if (userInfo && !userInfo.team) {
      setSharedConversations(prev => (prev.length === 0 ? prev : []));
      setSharedLoaded(true);
      return;
    }
    try {
      const rows = await getSharedConversations();
      setSharedConversations(rows);
    } catch (e) {
      if (e instanceof TeamUnauthorizedError) {
        navigate('/login');
        return;
      }
      setSharedConversations(prev => (prev.length === 0 ? prev : []));
    } finally {
      setSharedLoaded(true);
    }
  };

  // Silent refresh of the projects list (sidebar PROJECT section + top-bar
  // menu). Deliberately NOT gated on team membership: personal projects mean a
  // team-less account has projects of its own, and the same endpoint answers
  // for both. A 404 (backend without the route yet) simply means "no projects"
  // and is never surfaced.
  const refreshProjectsRef = useRef<() => Promise<void>>();
  refreshProjectsRef.current = async () => {
    if (!localStorage.getItem('accessToken')) return;
    try {
      const rows = await listProjects();
      setProjects(rows);
    } catch (e) {
      if (e instanceof TeamUnauthorizedError) {
        navigate('/login');
        return;
      }
      setProjects(prev => (prev.length === 0 ? prev : []));
    } finally {
      setProjectsLoaded(true);
    }
  };

  // Load once on mount and whenever team membership becomes known/changes.
  // Depends on the BOOLEAN, not on `userInfo.team` itself: the team object is
  // re-created by every credit refresh, which would re-fire this on each one.
  const hasTeam = !!userInfo?.team;
  useEffect(() => {
    refreshSharedConversationsRef.current?.();
    refreshProjectsRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTeam]);

  useEffect(() => {
    if (!hasTeam || !localStorage.getItem('accessToken')) {
      setPlaybooks([]);
      setMyPrograms([]);
      return;
    }
    let cancelled = false;
    Promise.all([listPlaybooks(), getMyPrograms()])
      .then(([payload, mine]) => {
        if (!cancelled) {
          setPlaybooks(payload.playbooks.filter((row) => row.status === 'published'));
          setMyPrograms(mine.programs || []);
        }
      })
      .catch((e) => {
        if (e instanceof TeamUnauthorizedError) {
          navigate('/login');
          return;
        }
        if (!cancelled) {
          setPlaybooks([]);
          setMyPrograms([]);
        }
      });
    return () => { cancelled = true; };
  }, [hasTeam, navigate]);

  // Ownership + visibility of a conversation, straight from the detail
  // endpoint. Applied only while that conversation is still the open one, so a
  // late response can never flip the read-only gate on a different view.
  const applyConversationMeta = useCallback((detail: ConversationDetail | null) => {
    setReadOnly(detail?.is_owner === false);
    setShareVisibility(detail?.visibility === 'team' ? 'team' : 'private');
    setSharedOwnerEmail(typeof detail?.owner_email === 'string' ? detail.owner_email : null);
    setConversationProjectId(
      typeof detail?.project_id === 'number' ? detail.project_id : null
    );
    setPlaybookRun(
      detail?.playbook_run && typeof detail.playbook_run.id === 'number'
        ? detail.playbook_run
        : null
    );
    setConversationTitle(
      typeof detail?.title === 'string' && detail.title.trim() ? detail.title : null
    );
    setBranchedFrom(
      detail?.branched_from && typeof detail.branched_from.uuid === 'string'
        ? { uuid: detail.branched_from.uuid, title: detail.branched_from.title ?? null }
        : null
    );
    setConversationMetaLoaded(true);
  }, []);

  const loadConversationMetaRef = useRef<(conversationId: number, conversationUuid: string) => Promise<void>>();
  loadConversationMetaRef.current = async (conversationId: number, conversationUuid: string) => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    if (!token) return;
    try {
      const resp = await fetch(`${BASE_URL}/conversations/uuid/${conversationUuid}`, {
        headers: { 'Authorization': `${tokenType} ${token}` }
      });
      if (!resp.ok) {
        // Unknown meta, but the conversation is still usable: release the
        // share pill rather than leaving it disabled on "Memuat…" forever.
        if (currentConversationIdRef.current === conversationId) {
          setConversationMetaLoaded(true);
        }
        return;
      }
      const detail: ConversationDetail = await resp.json();
      if (currentConversationIdRef.current !== conversationId) return;
      applyConversationMeta(detail);
    } catch {
      /* transient - the conversation just keeps its default (owner) treatment */
      if (currentConversationIdRef.current === conversationId) {
        setConversationMetaLoaded(true);
      }
    }
  };

  // Silent refresh of the conversations list (no skeleton/loading flags):
  // merge by id, server page order first, then locally-known older pages.
  const pollConversationsRef = useRef<() => Promise<void>>();
  pollConversationsRef.current = async () => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    if (!token) return;
    try {
      const resp = await fetch(`${BASE_URL}/conversations/?skip=0&limit=20`, {
        headers: { 'Authorization': `${tokenType} ${token}` }
      });
      if (!resp.ok) return;
      const data: Conversation[] = await resp.json();
      setConversations(prev => {
        const byId = new Map(prev.map(c => [c.id, c]));
        const serverIds = new Set(data.map(c => c.id));
        // Server order wins for its window; local fields (nothing today, but
        // future client-only annotations) survive under the server's values.
        const merged: Conversation[] = data.map(sc => ({ ...(byId.get(sc.id) ?? {}), ...sc }));
        for (const c of prev) {
          if (!serverIds.has(c.id)) merged.push(c); // pagination tail beyond page 1
        }
        return merged;
      });
    } catch { /* transient poll failure - next tick retries */ }
  };

  // The sidebar "Tim" block gets the RAW shared list. It used to receive an
  // ownership-filtered copy (own shared conversations dropped, because they
  // already have a row in the personal date-grouped list and the duplicate
  // drifted: only the personal row is refreshed by the run lifecycle). That
  // filter also hid own rows from the PROJECT they are filed in, so a project
  // with only your own conversations looked empty. Sidebar now groups instead:
  // own rows show nested under their project, and are excluded only from the
  // flat "Dibagikan" remainder where the duplicate actually happened.
  // /p/:id and /team are unaffected: they build their own lists.

  // Poll every ~12s WHILE something is in flight (a known running run, or a
  // local consumer streaming). Idle app -> no polling. BOTH lists count: a
  // teammate's run only ever shows up in the shared list, and if it did not
  // hold the poll open its row would freeze on the snapshot that first saw it
  // running. Own rows in the shared list overlap with the personal list here,
  // which is harmless: it is an OR over "is anything running".
  const anyRunActive =
    conversations.some(c => c.active_run_status === 'running') ||
    sharedConversations.some(r => r.active_run_status === 'running') ||
    isLoading;
  useEffect(() => {
    if (!anyRunActive) return;
    const t = setInterval(() => {
      pollConversationsRef.current?.();
      // The Tim block shows the same live-run treatment, so it refreshes on
      // the same tick as the personal list.
      refreshSharedConversationsRef.current?.();
    }, 12000);
    return () => clearInterval(t);
  }, [anyRunActive]);

  // uuid of the open conversation. The route param is authoritative (a shared
  // conversation is NOT in `conversations`, which is the personal list).
  const currentConversationUuid =
    conversations.find(c => c.id === currentConversationId)?.uuid || uuid || null;

  // Share toggle: PATCH visibility, then refresh the Tim block so the row
  // appears/disappears immediately instead of waiting for the next poll.
  const handleToggleShare = useCallback(async () => {
    if (sharePending || readOnly) return;
    if (!currentConversationUuid) {
      // Should not be reachable (the pill only renders on a loaded
      // conversation), but a control that does nothing at all is the one
      // outcome this must never have.
      setSurfaceNotice({
        text: 'Percakapan ini belum siap dibagikan. Tunggu sebentar, lalu coba lagi.',
        tone: 'error',
      });
      return;
    }
    // No browser dialog here any more: the "Dibagikan · Tim" pill in the top bar
    // flips to an inline "Yakin privat?" / "Batal" pair and only calls this once
    // the user has confirmed there.
    const next: ConversationVisibility = shareVisibility === 'team' ? 'private' : 'team';
    setSharePending(true);
    try {
      const res = await apiSetConversationVisibility(currentConversationUuid, next);
      const applied: 'team' | 'private' = res?.visibility === 'team' ? 'team' : 'private';
      setShareVisibility(applied);
      // Mirror onto the sidebar row so its shared-to-team glyph flips live
      // (the list otherwise learns the new visibility only on the next fetch).
      setConversations(prev => prev.map(conv =>
        conv.uuid === currentConversationUuid ? { ...conv, visibility: applied } : conv
      ));
      refreshSharedConversationsRef.current?.();
    } catch (e) {
      if (e instanceof TeamUnauthorizedError) {
        navigate('/login');
        return;
      }
      setSurfaceNotice({
        text:
          e instanceof TeamError && e.detail
            ? e.detail
            : 'Gagal mengubah status berbagi chat ini. Coba lagi.',
        tone: 'error',
      });
    } finally {
      setSharePending(false);
    }
  }, [currentConversationUuid, sharePending, readOnly, shareVisibility, navigate]);

  useEffect(() => {
    if (!currentConversationUuid || readOnly) {
      setLinkShareActive(false);
      return;
    }
    let cancelled = false;
    getOwnerShare(currentConversationUuid)
      .then((row) => {
        if (!cancelled) setLinkShareActive(!!row.active);
      })
      .catch(() => {
        if (!cancelled) setLinkShareActive(false);
      });
    return () => { cancelled = true; };
  }, [currentConversationUuid, readOnly]);

  // Move the open conversation into a project (or out of every project with
  // null). A TEAM project auto-shares the conversation server-side, so we ask
  // first and then adopt the visibility the server reports back rather than
  // guessing it. Removing from a project never unshares.
  const handleSelectProject = useCallback(async (projectId: number | null) => {
    if (!currentConversationUuid || projectPending || readOnly) return;
    if (projectId === conversationProjectId) return;
    // The "this will share the chat" confirmation is inline in the assign menu
    // (the row flips to "Yakin pindahkan?" / "Batal"); by the time we get here
    // the answer is already yes. projectNeedsShareConfirm below is the predicate
    // the menu asks.
    setProjectPending(true);
    try {
      const res = await apiSetConversationProject(currentConversationUuid, projectId);
      const nextProjectId = typeof res?.project_id === 'number' ? res.project_id : null;
      setConversationProjectId(nextProjectId);
      setShareVisibility(res?.visibility === 'team' ? 'team' : 'private');
      // The sidebar nests off the PERSONAL list's project_id, so without this
      // the row stayed in the date groups until the next poll: the chat looked
      // like it had not moved at all. Optimistic stamp now, server truth on the
      // refresh right after.
      setConversations(prev => prev.map(c => (
        c.id === currentConversationId ? { ...c, project_id: nextProjectId } : c
      )));
      if (nextProjectId != null) expandProject(nextProjectId);
      refreshSharedConversationsRef.current?.();
      refreshProjectsRef.current?.();
      void pollConversationsRef.current?.();
    } catch (e) {
      if (e instanceof TeamUnauthorizedError) {
        navigate('/login');
        return;
      }
      setSurfaceNotice({
        text:
          e instanceof TeamError && e.detail
            ? e.detail
            : 'Gagal memindahkan chat ke project. Coba lagi.',
        tone: 'error',
      });
    } finally {
      setProjectPending(false);
    }
  }, [
    currentConversationUuid,
    currentConversationId,
    projectPending,
    readOnly,
    conversationProjectId,
    expandProject,
    setConversations,
    navigate,
  ]);

  // Does moving this chat into `projectId` also SHARE it with the team? The
  // assign menu asks before it commits, and turns its own row into the confirm
  // instead of raising a browser dialog.
  const projectNeedsShareConfirm = useCallback(
    (projectId: number): boolean => {
      const target = projects.find((p) => p.id === projectId) || null;
      return !!target && target.visibility !== 'private' && shareVisibility !== 'team';
    },
    [projects, shareVisibility]
  );

  // Create a project from anywhere in the shell: the sidebar's inline draft row
  // and the top-bar assign menu's "Project baru…" both land here, so there is
  // exactly one create path and one refresh. Returns the new row (or null on
  // failure) so the caller can assign to it / expand it.
  const handleCreateProject = useCallback(
    async (name: string, visibility?: ProjectVisibility): Promise<ProjectRow | null> => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      try {
        // A team-less account gets a personal project; the backend picks that
        // default when no visibility is sent, so we only pass one for teams.
        const created = await apiCreateProject(
          visibility ? { name: trimmed, visibility } : { name: trimmed }
        );
        await refreshProjectsRef.current?.();
        if (created?.id != null) expandProject(created.id);
        return created ?? null;
      } catch (e) {
        if (e instanceof TeamUnauthorizedError) {
          navigate('/login');
          return null;
        }
        setSurfaceNotice({
          text:
            e instanceof TeamError && e.detail
              ? e.detail
              : 'Gagal membuat project baru. Coba lagi.',
          tone: 'error',
        });
        return null;
      }
    },
    [expandProject, navigate]
  );

  // After a conversation's messages load, check whether a run is still active
  // for it and re-attach (rebuilding trace + partial text + live continuation
  // via the stream replay). Stored in a ref so callers always hit the latest
  // closure. Returns nothing; fire-and-forget consumption is self-managing.
  const maybeReattachActiveRunRef = useRef<(conversationId: number, conversationUuid: string) => Promise<void>>();
  maybeReattachActiveRunRef.current = async (conversationId: number, conversationUuid: string) => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    if (!token) return;
    const lsKey = `activeRun:${conversationId}`;
    try {
      const resp = await fetch(`${BASE_URL}/ai/conversations/${conversationId}/active-run`, {
        headers: { 'Authorization': `${tokenType} ${token}` }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const runId: string | null = data?.run_id ?? null;
      const status: string | null = data?.status ?? null;

      // The user may have navigated elsewhere while we were querying,
      // nothing here may touch the view of a different conversation.
      if (currentConversationIdRef.current !== conversationId) return;

      if (runId && status === 'running') {
        // Already consuming this run in this tab? Don't attach a second stream;
        // just make sure the in-progress bubble wasn't wiped by a concurrent
        // messages replacement (the consumer also self-heals by id on its next
        // event, but this covers silent phases with no events in flight).
        const existingPlaceholderId = attachedRunsRef.current.get(runId);
        if (existingPlaceholderId !== undefined) {
          setMessages(prev => prev.some(m => m.id === existingPlaceholderId)
            ? prev
            : [...prev, {
                id: existingPlaceholderId,
                role: 'assistant',
                content: '',
                model: 'auto',
                isComplete: false,
                created_at: new Date().toISOString()
              } as MessageWithCitations]);
          return;
        }
        const placeholderId = Date.now() + 1;
        attachedRunsRef.current.set(runId, placeholderId);
        localStorage.setItem(lsKey, runId);
        // Sidebar: reflect the live run immediately (poll would lag ~12s).
        setConversations(prev => prev.map(c =>
          c.id === conversationId ? { ...c, active_run_status: 'running' as const } : c
        ));
        // Append an in-progress assistant placeholder; the replay fills it in.
        // The consumer targets it BY ID, so even if a racing refetch replaces
        // the list, the next stream event restores it in the right position.
        setMessages(prev => [...prev, {
          id: placeholderId,
          role: 'assistant',
          content: '',
          model: 'auto',
          isComplete: false,
          created_at: new Date().toISOString()
        } as MessageWithCitations]);
        setIsLoading(true);
        consumeRunStreamRef.current(runId, {
          isReattach: true,
          conversationId,
          conversationUuid,
          assistantMessageId: placeholderId
        })
          .catch(err => console.error('re-attach run stream failed:', err))
          // Release the guard once local consumption ends (completed, errored,
          // or reader aborted by leaving the conversation) so returning to the
          // conversation while the server run is still going can re-attach.
          .finally(() => { attachedRunsRef.current.delete(runId); });
      } else if (localStorage.getItem(lsKey)) {
        // We had a pointer but the server reports no running run - it finished
        // while we were away. Clear the pointer and refetch (the answer is
        // already in the freshly-loaded messages, but refetch guarantees it).
        localStorage.removeItem(lsKey);
        await fetchConversationMessages(conversationId, conversationUuid, null, 'next', 50, false);
      }
    } catch (e) {
      console.error('active-run check failed:', e);
    }
  };

  // Mobile browsers freeze JavaScript and often tear down long-lived fetch/SSE
  // sockets while backgrounded. The run itself is server-side, so on resume we
  // deliberately discard the possibly-dead LOCAL reader, reload persisted
  // messages, and attach a fresh replay stream. This also covers pages restored
  // from Safari's back-forward cache and an offline -> online transition.
  useEffect(() => {
    let recovering = false;
    let lastRecoveryAt = 0;

    const recoverVisibleConversation = async () => {
      if (recovering || document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastRecoveryAt < 1_000) return;
      const conversationId = currentConversationIdRef.current;
      const conversationUuid =
        conversations.find(c => c.id === conversationId)?.uuid || uuid || null;
      if (conversationId == null || !conversationUuid) return;

      recovering = true;
      lastRecoveryAt = now;
      try {
        // Abort only the browser-side replay connection. /chat/start is no
        // longer wired to this controller, and the backend worker keeps going.
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        attachedRunsRef.current.clear();
        await new Promise(resolve => setTimeout(resolve, 0));
        await fetchConversationMessages(
          conversationId, conversationUuid, null, 'next', 50, false
        );
        await maybeReattachActiveRunRef.current?.(conversationId, conversationUuid);
        void pollConversationsRef.current?.();
        void refreshSharedConversationsRef.current?.();
      } finally {
        recovering = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void recoverVisibleConversation();
    };
    const onPageShow = () => { void recoverVisibleConversation(); };
    const onOnline = () => { void recoverVisibleConversation(); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', onOnline);
    };
    // Conversation identity changes reinstall this listener with the current
    // uuid/list snapshot. fetchConversationMessages is intentionally omitted:
    // it is recreated each render and would churn global event listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, uuid, conversations]);

  // Compare-mode submit: when user has 2-3 models picked, fan out to
  // /api/v1/ai/compare/stream and update N placeholder assistant messages
  // as chunks arrive (each tagged with `model`).
  const submitCompare = useCallback(async (message: string) => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    if (!token) { navigate('/login'); return; }
    if (!message.trim() || isLoading) return;

    setIsLoading(true);

    // Ensure conversation exists; create one when needed.
    let convId = currentConversationId;
    let convUuid: string | null = null;
    if (!convId) {
      try {
        const createResp = await fetch(`${config.apiUrl}/conversations/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `${tokenType} ${token}` },
          body: JSON.stringify({ title: message.slice(0, 60) }),
        });
        if (!createResp.ok) throw new Error(`Create conv ${createResp.status}`);
        const data = await createResp.json();
        convId = data.id;
        convUuid = data.uuid;
        setCurrentConversationId(convId);
        // Optimistically tag with context_type='compare' so the sidebar badge
        // shows immediately - backend marks the row to the same value at the
        // start of the compare stream call.
        setConversations((prev: Conversation[]) => [
          { ...data, context_type: 'compare' } as Conversation,
          ...prev,
        ]);
      } catch (e) {
        console.error('compare: failed to create conversation', e);
        setIsLoading(false);
        return;
      }
    } else {
      // Existing conversation getting its first compare turn - flip the
      // local entry's context_type so the badge appears without a refresh.
      setConversations((prev: Conversation[]) =>
        prev.map((c) =>
          c.id === convId && c.context_type !== 'compare'
            ? ({ ...c, context_type: 'compare' } as Conversation)
            : c
        )
      );
    }

    // Optimistic UI: 1 user msg + N placeholder assistant msgs (one per model).
    const baseId = Date.now();
    const userMsg: MessageWithCitations = {
      id: baseId,
      role: 'user',
      content: message,
      model: selectedModels.join(','),
      isComplete: true,
      created_at: new Date().toISOString(),
    } as MessageWithCitations;
    const placeholders: MessageWithCitations[] = selectedModels.map((m, i) => ({
      id: baseId + i + 1,
      role: 'assistant',
      content: '',
      model: m,
      isComplete: false,
      created_at: new Date().toISOString(),
    }) as MessageWithCitations);
    setMessages((prev) => [...prev, userMsg, ...placeholders]);
    setInputMessage('');
    resetTextAreaHeight();
    // Stop the auto-scroll-on-message-change useEffect from yanking the
    // viewport while 3 streams write rapidly. User keeps scroll control.
    setShouldScroll(false);

    const modelToId: Record<string, number> = {};
    selectedModels.forEach((m, i) => { modelToId[m] = baseId + i + 1; });

    try {
      const resp = await fetch(`${config.apiUrl}/ai/compare/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `${tokenType} ${token}` },
        body: JSON.stringify({
          message,
          models: selectedModels,
          conversation_id: convId,
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${txt.slice(0, 200)}`);
      }
      const reader = resp.body?.getReader();
      if (!reader) throw new Error('No stream reader');
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (!payload) continue;
          try {
            const parsed = JSON.parse(payload);
            const id = modelToId[parsed.model];
            if (!id) continue;
            setMessages((prev) =>
              prev.map((msg) => {
                if (msg.id !== id) return msg;
                const updated: MessageWithCitations = { ...msg };
                if (typeof parsed.text === 'string' && !parsed.done) {
                  updated.content = (updated.content || '') + parsed.text;
                }
                if (parsed.done) updated.isComplete = true;
                if (parsed.error) {
                  updated.error = true;
                  updated.content = `Error: ${parsed.error}`;
                  updated.isComplete = true;
                }
                return updated;
              })
            );
          } catch (e) {
            console.warn('compare: parse error', e, payload);
          }
        }
      }
    } catch (e: any) {
      console.error('compare: stream failed', e);
      // Mark all pending placeholders as errored
      setMessages((prev) => prev.map((msg) =>
        Object.values(modelToId).includes(msg.id) && !msg.isComplete
          ? { ...msg, error: true, content: `Error: ${e?.message || e}`, isComplete: true }
          : msg
      ));
    } finally {
      setIsLoading(false);
      // Navigate to new conversation URL if we just created one
      if (convUuid) {
        try { navigate(`/c/${convUuid}`); } catch {}
      }
    }
  }, [currentConversationId, isLoading, navigate, selectedModels, setConversations, setCurrentConversationId, setIsLoading, setMessages]);

  // Wrapper: route to compare or single based on selection count and whether
  // the user is uploading files (compare doesn't support files yet - falls
  // back to single-mode handler with the first model).
  const handleSubmit = useCallback(async (
    e: React.FormEvent,
    forcedModel?: Model,
    forcedMessage?: string
  ) => {
    // Read-only shared conversation: every send route lands here (composer
    // button, Enter key, model-switch resend), so one guard closes them all.
    if (readOnly) {
      e?.preventDefault?.();
      return;
    }
    const isCompare = selectedModels.length > 1 && selectedFiles.length === 0 && !forcedModel;
    if (isCompare) {
      e?.preventDefault?.();
      await submitCompare(forcedMessage ?? inputMessage);
      return;
    }
    return handleSubmitSingle(e, forcedModel, forcedMessage);
  }, [selectedModels, selectedFiles, inputMessage, submitCompare, handleSubmitSingle, readOnly]);

  // Update the copyToClipboard function to track copying
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
    
    // Track content copying
    mixpanel.track('Content Copied', {
      content_length: text.length,
      content_type: 'code',
      model: selectedModel,
      conversation_id: currentConversationId,
      user_email: userInfo?.email
    });
    
    // Increment copy count
    mixpanel.people.increment('Total Copies');
  };


  // Helper function to convert citations Set to citationsMap object
  const convertCitationsToMap = (citations?: Set<string>): { [key: string]: any } | undefined => {
    if (!citations || citations.size === 0) return undefined;
    
    const citationsMap: { [key: string]: any } = {};
    Array.from(citations).forEach((source, index) => {
      citationsMap[String(index + 1)] = {
        url: source,
        title: source
      };
    });
    return citationsMap;
  };


  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);


  // Update the renderMessage function to handle large tables differently
  // Set up message renderer hook
  const messageRendererDependencies: MessageRendererDependencies = {
    theme,
    currentThemeColors,
    messageStyles,
    copiedMessageId,
    branchingMessageId,
    openModelSwitchId,
    copiedCode,
    isMobile,
    messages,
    textareaRef,
    getModelDisplayName,
    convertCitationsToMap,
    copyToClipboard,
    // Teams phase 2 leftover: the feedback thumbs are hidden on someone else's
    // conversation (the server 403s them anyway).
    readOnly
  };

  const messageRendererCallbacks: MessageRendererCallbacks = {
    setCopiedMessageId,
    setBranchingMessageId,
    setOpenModelSwitchId,
    setPopupPosition,
    setInputMessage,
    handleBranchout,
    navigate
  };

  const { renderMessage } = useMessageRenderer(messageRendererDependencies, messageRendererCallbacks);




  // Update the deleteConversation function
  //
  // No confirmation here: the sidebar row's × flips into an inline
  // "Yakin hapus?" / "Batal" pair and only calls this after the user says yes.
  const deleteConversation = async (conversationId: number) => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    if (!token) return;

    try {
      const response = await fetch(`${BASE_URL}/conversations/${conversationId}`, {
        method: 'DELETE',
                    headers: {
          'Authorization': `${tokenType} ${token}`
        }
      });
      
      if (response.ok) {
        setConversations(prev => prev.filter(conv => conv.id !== conversationId));
        if (currentConversationId === conversationId) {
          // The open conversation is gone: leave its route and clear every
          // trace of it. Without the navigate the URL still pointed at the
          // deleted uuid, so the crumb kept its title and a reload bounced
          // through a 404.
          setCurrentConversationId(null);
          currentConversationIdRef.current = null;
          setMessages([]);
          // The thread is empty now, so the scroll-to-bottom arrow has nothing
          // to scroll to; it would otherwise linger over the welcome screen.
          setShowScrollToBottom(false);
          setConversationTitle(null);
          setBranchedFrom(null);
          setReadOnly(false);
          setSharedOwnerEmail(null);
          setShareVisibility('private');
          setConversationProjectId(null);
          setPlaybookRun(null);
          setConversationMetaLoaded(true);
          localStorage.removeItem('lastConversationId');
          localStorage.removeItem('lastConversationUuid');
          navigate('/c');
        }
        // The Tim block and the project counts both include this row.
        refreshSharedConversationsRef.current?.();
        refreshProjectsRef.current?.();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }

    // Add tracking to deleteConversation
    mixpanel.track('Conversation Deleted', {
      conversation_id: conversationId,
      user_email: userInfo?.email,
      user_status: userInfo?.status
    });
  };

  // Add new loading state
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Add new interfaces for paginated response
  interface PaginatedMessages {
    messages: MessageWithCitations[];
    next_cursor: string | null;
    previous_cursor: string | null;
    has_next: boolean;
    has_previous: boolean;
  }

  // Add this state to track if we're already fetching messages
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);

  // Update fetchConversationMessages function to use the service
  const fetchConversationMessages = async (
    conversationId: number,
    conversationUuid: string,
    cursor?: string | null,
    direction: 'next' | 'previous' = 'next',
    pageSize: number = 10,
    shouldAutoScroll: boolean = true
  ) => {
    if (isFetchingMessages) return;

    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    if (!token || !tokenType) {
      navigate('/login');
      return;
    }

    if (direction === 'previous') {
      setShouldScroll(false);
    }

    const params: FetchMessagesParams = {
      conversationId,
      conversationUuid,
      cursor,
      direction,
      pageSize,
      shouldAutoScroll
    };

    const callbacks = {
      onStart: () => {
        setIsFetchingMessages(true);
        setIsLoadingMessages(true);
      },
      onFinish: () => {
        setIsLoadingMessages(false);
        setIsFetchingMessages(false);
      },
      onError: (error: any) => {
        console.error('Error fetching conversation messages:', error);
      },
      onUnauthorized: () => {
        navigate('/login');
      }
    };

    const result = await fetchMessages(params, token, tokenType, callbacks, chatContainerRef);
    
    if (result) {
      const { messages: processedMessages, paginationData, scrollData } = result;
      
      // If it's a pagination request, merge with existing messages
      if (cursor) {
        setMessages(prev => 
          direction === 'previous' 
            ? [...processedMessages, ...prev]
            : [...prev, ...processedMessages]
        );

        // Restore scroll position after loading previous messages
        if (direction === 'previous' && chatContainerRef.current && scrollData) {
          restoreScrollPosition(chatContainerRef.current, scrollData);
        }
      } else {
        // Initial load - replace messages
        setMessages(processedMessages);
        setShouldScroll(shouldAutoScroll);
      }
      
      setPaginationData(paginationData);
    }
  };

  // Add handler for conversation selection
  const handleConversationSelect = async (conversationId: number, conversationUuid: string) => {

    if (currentConversationId === conversationId) return;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // The aborted consumer no longer clears isLoading when its conversation is
    // off-screen (per-view gating) - reset it here for the incoming view; the
    // reattach flow flips it back on if this conversation has a live run.
    setIsLoading(false);
    setUploadError(null); // drop any stale composer upload error

    setIsLoadingConversation(true);
    setIsSwitchingConversation(true);
    setCurrentConversationId(conversationId);
    currentConversationIdRef.current = conversationId; // eager: consumers gate on this
    localStorage.setItem('lastConversationUuid', conversationUuid);

    // Rows in this list are always the viewer's own conversations, so start
    // from the owner defaults and let the detail fetch fill in visibility.
    setReadOnly(false);
    setSharedOwnerEmail(null);
    setShareVisibility('private');
    // Those are DEFAULTS, not facts, until the detail lands: hold the share
    // pill closed so a click cannot act on a guess.
    setConversationMetaLoaded(false);
    loadConversationMetaRef.current?.(conversationId, conversationUuid);
    
    // Update URL first before fetching
    navigate(`/c/${conversationUuid}`);
    
    try {
      await fetchConversationMessages(conversationId, conversationUuid, null, 'next', 50, true);

      // Re-attach if a run is still active for the conversation being opened.
      await maybeReattachActiveRunRef.current?.(conversationId, conversationUuid);

      // Model selection is 'auto' only now, so there is nothing to downgrade.
      // Guard kept for any legacy non-auto selection but is effectively inert.
      if (userInfo?.status === 'lite' && selectedModel !== 'auto' && !isModelLite(selectedModel)) {
        // Switch to a default lite model (first free model in the list)
        const allModels = modelCategories.flatMap(cat => cat.models);
        const firstFreeModel = allModels.find(m => m.credits === 'unlimited')?.value || allModels[0]?.value || 'gpt-5.2';
        setSelectedModel(firstFreeModel as Model);
        alert('Switched to a free model due to subscription restrictions. Please upgrade to PRO to use premium models.');
      }
      
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    } finally {
      setIsLoadingConversation(false);
      setIsSwitchingConversation(false);
    }

    mixpanel.track('Conversation Selected', {
      conversation_id: conversationId,
      conversation_uuid: conversationUuid,
      user_email: userInfo?.email,
      user_status: userInfo?.status
    });
  };

  // Update handleNewChat function
  //
  // A new chat INHERITS the project you are standing in (blueprint 3.4): from
  // /p/:id, or from a chat that is filed in a project. The context rides in the
  // URL (/c?project=:id) so the welcome screen can show a removable pill and a
  // reload keeps it.
  // `explicitProjectId` is passed by the sidebar's per-project "+" (and its
  // empty-state row): "start a chat in THAT project", regardless of where the
  // user is standing. Never wire this to onClick directly - React would hand it
  // the mouse event; the shell exposes handleNewChat / handleNewChatInProject.
  const startNewChat = (explicitProjectId?: number | null) => {
    const inheritedProjectId =
      explicitProjectId !== undefined
        ? explicitProjectId
        : panelProjectId ?? (isChatSurface ? conversationProjectId : null);

    // Behave like a conversation switch: detach the local reader of any run
    // streaming in the current conversation (the SERVER run keeps going,
    // reopening that conversation reattaches via active-run) and reset the
    // composer state for the fresh view.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setUploadError(null); // drop any stale composer upload error
    currentConversationIdRef.current = null; // eager: gate closes before next render

    setCurrentConversationId(null);
    setMessages([]);
    // A brand-new chat is always mine and always private. That IS the fact
    // here, so the meta counts as loaded (no detail to fetch yet).
    setReadOnly(false);
    setSharedOwnerEmail(null);
    setShareVisibility('private');
    setConversationMetaLoaded(true);
    // ...and it has no title yet. Without this the crumb kept showing the
    // PREVIOUS conversation's title on the fresh welcome screen (P1-4).
    setConversationTitle(null);
    setBranchedFrom(null);
    // ...and belongs to no project until its first message says otherwise,
    // so the top-bar pill can never carry over the previous conversation's.
    setConversationProjectId(null);
    setPlaybookRun(null);
    setPaginationData({
      nextCursor: null,
      previousCursor: null,
      hasNext: false,
      hasPrevious: false
    });
    // Clear both ID and UUID from localStorage
    localStorage.removeItem('lastConversationId');
    localStorage.removeItem('lastConversationUuid');
    
    // Reset background and persona in localStorage
    localStorage.removeItem('userBackground');
    localStorage.removeItem('assistantPersona');
    
    // Model selection is 'auto' only now, so there is nothing to downgrade.
    // Guard kept for any legacy non-auto selection but is effectively inert.
    if (userInfo?.status === 'lite' && selectedModel !== 'auto' && !isModelLite(selectedModel)) {
      // Switch to a default lite model (first free model in the list)
      const allModels = modelCategories.flatMap(cat => cat.models);
      const firstFreeModel = allModels.find(m => m.credits === 'unlimited')?.value || allModels[0]?.value || 'gpt-5.2';
      setSelectedModel(firstFreeModel as Model);
      alert('Switched to a free model due to subscription restrictions. Please upgrade to PRO to use premium models.');
    }
    
    // Close sidebar in mobile view
    if (isMobile) {
      setIsSidebarOpen(false);
    }
    
    // Navigate to base chat URL, carrying the project context when there is one.
    navigate(inheritedProjectId != null ? `/c?project=${inheritedProjectId}` : '/c');
    setPendingProjectId(inheritedProjectId);

    // Focus the textarea after clearing
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);

    mixpanel.track('New Chat Started', {
      user_email: userInfo?.email,
      user_status: userInfo?.status,
      previous_conversation_id: currentConversationId,
      project_id: inheritedProjectId
    });
  };

  // Event-handler-safe wrapper (no positional argument to hijack).
  const handleNewChat = () => startNewChat();

  // "Chat baru di project ini" from a sidebar project row: the full new-chat
  // reset, then the welcome screen with that project's pill.
  const handleNewChatInProject = useCallback(
    (projectId: number) => {
      expandProject(projectId);
      startNewChat(projectId);
    },
    // startNewChat closes over render state on purpose (same as handleNewChat).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expandProject, panelProjectId, isChatSurface, conversationProjectId, isMobile, userInfo, selectedModel, currentConversationId]
  );

  // Add function to update conversation title
  const updateConversationTitle = async (conversationId: number, newTitle: string) => {
    await titleUpdateConversationTitle(conversationId, newTitle, titleManagementCallbacks);
  };

  // Add handler for title edit submission
  const handleTitleSubmit = (conversationId: number, event: React.KeyboardEvent) => {
    titleHandleTitleSubmit(conversationId, event, titleManagementConfig, titleManagementCallbacks);
  };

  // Add this function to handle starting edit mode
  const startEditing = (e: React.MouseEvent, conversation: Conversation) => {
    titleStartEditing(e, conversation, titleManagementCallbacks);
  };

  // Persist multi-model selection (and the legacy single key for back-compat
  // with any code that still reads it before this rewrite ships).
  useEffect(() => {
    localStorage.setItem('lastSelectedModels', JSON.stringify(selectedModels));
    if (selectedModels[0]) {
      localStorage.setItem('lastSelectedModel', selectedModels[0]);
    }
  }, [selectedModels]);



  const handleTitleUpdate = async (conversationId: number) => {
    await titleHandleTitleUpdate(conversationId, titleManagementConfig, titleManagementCallbacks);
  };


  const truncateFilename = (filename: string, maxLength: number = 20): string => {
    if (filename.length <= maxLength) return filename;
    const extension = filename.split('.').pop() || '';
    const nameWithoutExt = filename.slice(0, filename.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 3) + '...';
    return `${truncatedName}.${extension}`;
  };

  // Tambahkan state untuk hover
  const [hoveredConversationId, setHoveredConversationId] = useState<number | null>(null);

  // Add logout function
  const handleLogout = () => {
    authHandleLogout(authConfig, authCallbacks);
  };

  // Add this style tag for the spinner animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0% { opacity: 0.8; }
        50% { opacity: 0.4; }
        100% { opacity: 0.8; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add useEffect to update CSS variables when theme changes
  useEffect(() => {
    document.documentElement.style.setProperty('--code-bg', currentThemeColors.codeBg);
  }, [currentThemeColors.codeBg]);

  // Add state for switching conversations
  const [isSwitchingConversation, setIsSwitchingConversation] = useState(false);

  // Helper function to identify Perplexity models (any model containing 'sonar')
  const isPerplexityModel = (model: string) => {
    return model.toLowerCase().includes('sonar') || 
           model.toLowerCase().includes('power-ai-chosen-model');
  };

  // Helper function to identify Claude models
  const isClaudeModel = (model: string) => {
    return model.toLowerCase().includes('claude');
  };

  // Helper function to identify image generation models
  const isImageGenerationModel = (model: string) => {
    return (
      model === 'gpt-image-1' ||
      model === 'gpt-image-2' ||
      model === 'black-forest-labs/FLUX.1-kontext-max' ||
      model === 'black-forest-labs/FLUX.2-pro' ||
      model === 'powerai-image-1' ||
      model === 'google/gemini-3.1-flash-image-preview'
    );
  };

  // Helper function to identify models that should show browser icon
  const shouldShowBrowserIcon = (model: string) => {
    const modelLower = model.toLowerCase();
    return (modelLower.includes('sonar') || 
            modelLower.includes('search')) && !isImageGenerationModel(model);
  };

  // Add useEffect for initial scroll to bottom
  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current && !isLoadingMessages && shouldScroll) {
      messagesEndRef.current.scrollIntoView();
    }
  }, [isInitialized, isLoadingMessages, shouldScroll]); // Add shouldScroll to dependencies

  // Add new state for initial page load
  const [isPageLoading, setIsPageLoading] = useState(true);

  // Update the initializeChat function in the useEffect
  //
  // Keep this the SINGLE init effect (see the note near the top of the file).
  // On the team/project surfaces it must NOT run its conversation restore: the
  // route carries no uuid there, so the localStorage fallback would happily
  // navigate the user out of the panel they just opened.
  //
  // Bare /c: the ONLY reason to leave the blank composer is a run that is still
  // going. Returns the uuid to resume, or null (and drops the stale pointer).
  const resumableConversationUuid = useCallback(async (): Promise<string | null> => {
    const storedUuid = localStorage.getItem('lastConversationUuid');
    const storedId = Number(localStorage.getItem('lastConversationId'));
    if (!storedUuid || !Number.isFinite(storedId) || storedId <= 0) return null;
    const lsKey = `activeRun:${storedId}`;
    const localRunPointer = localStorage.getItem(lsKey);
    if (!localRunPointer) return null;
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    if (!token) return null;
    try {
      const resp = await fetch(`${BASE_URL}/ai/conversations/${storedId}/active-run`, {
        headers: { 'Authorization': `${tokenType} ${token}` }
      });
      if (!resp.ok) {
        localStorage.removeItem(lsKey);
        return null;
      }
      const data = await resp.json();
      if (data?.run_id && data?.status === 'running') return storedUuid;
      // "pending" means the browser was suspended before /chat/start returned.
      // Even if the run already finished outside the short active-run window,
      // reopen this conversation once so its persisted answer (or visibly empty
      // state when the POST never arrived) is not hidden behind a blank /c.
      if (localRunPointer === 'pending') {
        localStorage.removeItem(lsKey);
        return storedUuid;
      }
      // Finished (or never started) while we were away: the pointer is a lie.
      localStorage.removeItem(lsKey);
      return null;
    } catch {
      // Offline / transient: do NOT hijack /c on a guess.
      return null;
    }
  }, []);

  useEffect(() => {
    if (surface !== 'chat') {
      if (conversations.length === 0) void fetchConversations(1);
      setIsInitialized(true);
      setIsPageLoading(false);
      setIsLoadingConversation(false);
      return;
    }
    const initializeChat = async () => {
      try {
        console.log('Initializing chat...');


        setMessages([]); 
        setConversationPage(1);
        setIsLoadingConversation(true);
                
        // The uuid in the route is always authoritative. A BARE /c is a request
        // for a new chat, so the localStorage pointer may only pull the user
        // back into an old conversation when that conversation genuinely has a
        // run still going - otherwise a leftover `activeRun:{id}` key (or just
        // yesterday's pointer) hijacked /c and the user never got the blank
        // composer they asked for. resumableUuid() answers that question and
        // clears the stale pointer on its way out.
        const targetUuid = uuid || (await resumableConversationUuid());

        if (conversations.length === 0) {
          await fetchConversations(1);
        }

        if (targetUuid) {
          // Fetch conversation details first
          const token = localStorage.getItem('accessToken');
          const tokenType = localStorage.getItem('tokenType');
          
          const response = await fetch(`${BASE_URL}/conversations/uuid/${targetUuid}`, {
            headers: {
              'Authorization': `${tokenType} ${token}`
            }
          });

          if (response.ok) {
            const conversation: ConversationDetail = await response.json();
            setCurrentConversationId(conversation.id);
            currentConversationIdRef.current = conversation.id;
            // Teams phase 2: ownership decides read-only, visibility drives the
            // share toggle. Applied BEFORE the messages load so the composer is
            // never briefly mounted on someone else's conversation.
            applyConversationMeta(conversation);
            await fetchConversationMessages(conversation.id, targetUuid);

            // If a run is still active for this conversation (tab was closed /
            // refreshed mid-run), re-attach: rebuild trace + partial text and
            // continue live via the stream replay. Read-only viewers take the
            // exact same path - watching a shared run live is the point.
            await maybeReattachActiveRunRef.current?.(conversation.id, targetUuid);

            // Update URL if we're using localStorage's UUID
            if (!uuid && targetUuid) {
              navigate(`/c/${targetUuid}`);
            }
          } else if (response.status === 403 || response.status === 404) {
            // Never had access, or it was just made private again. Drop the
            // pointer and fall back to a fresh chat, silently (no toast).
            localStorage.removeItem('lastConversationUuid');
            setCurrentConversationId(null);
            currentConversationIdRef.current = null;
            applyConversationMeta(null);
            navigate('/c');
          }
        }
        
        setIsInitialized(true);
                } finally {
        setIsLoadingConversation(false); // Add this
        setIsPageLoading(false);
      }
    };

    initializeChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, navigate, surface]);

  // Add this useEffect to inject the CSS styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .load-more-button {
        padding: 10px 20px;
        border-radius: 8px;
        border: none;
        background-color: ${theme === 'light' ? '#F0F0F0' : '#2D2D2D'};
        color: ${theme === 'light' ? '#1A1A1A' : '#FFFFFF'};
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s ease;
        box-shadow: ${theme === 'light' 
          ? '0 2px 6px rgba(0, 0, 0, 0.05)' 
          : '0 2px 6px rgba(0, 0, 0, 0.2)'};
        opacity: 1;
      }

      .load-more-button:hover {
        background-color: ${theme === 'light' ? '#E5E5E5' : '#383838'};
        transform: translateY(-1px);
        box-shadow: ${theme === 'light'
          ? '0 4px 12px rgba(0, 0, 0, 0.1)'
          : '0 4px 12px rgba(0, 0, 0, 0.3)'};
      }

      .load-more-button:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [theme]);

  // Update the intersection observer setup
  useEffect(() => {
    const options = {
      root: document.querySelector('.custom-scrollbar'), // Target the scrollable container
      rootMargin: '100px', // Increase margin to trigger earlier
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && hasMoreConversations && !isLoadingConversations) {
          loadMoreConversations();
        }
      });
    }, options);

    // Find the sentinel element
    const sentinel = document.querySelector('#conversations-sentinel');
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => observer.disconnect();
  }, [hasMoreConversations, isLoadingConversations, loadMoreConversations]);

  // Add this useEffect near the top of your component, with the other useEffects
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Custom Scrollbar Styles - thin, rounded, translucent, no track */
      .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }

      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }

      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: ${theme === 'light' ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.16)'};
        border-radius: 999px;
        transition: background 0.2s ease;
      }

      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: ${theme === 'light' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.28)'};
      }

      /* For Firefox */
      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: ${theme === 'light' ? 'rgba(0,0,0,0.18) transparent' : 'rgba(255,255,255,0.16) transparent'};
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [theme]);

  
  const isImageFile = (fileName: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  // Use the imported getModelIcon function instead of redefining it
  // This ensures we're using the same function with consistent arguments
  // throughout the codebase
  const getModelIcon = configGetModelIcon;


  // Initial loading screen
  if (isPageLoading) {
    return (
      <div style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: currentThemeColors.background,
        color: currentThemeColors.text,
        gap: '20px'
      }}>
        <img
          src={theme === 'dark' ? '/logo-only200-removebg-inverted.png' : '/logo-only200-removebg.png'}
          alt="Logo"
          style={{
            width: '40px',
            height: '40px',
            objectFit: 'contain'
          }}
        />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px'
        }}>
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24"
              style={{
              animation: 'spin 1s linear infinite',
              opacity: '0.7'
            }}
          >
            <circle 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="3" 
              fill="none" 
              strokeDasharray="30 50"
            />
          </svg>
          <span style={{ 
            fontSize: '16px',
            opacity: '0.8'
          }}>
            Loading Power AI...
          </span>
        </div>
      </div>
    );
  }



  // Update the conversations list rendering section
  // Replace the existing conversations mapping with this:
  return (
    <div style={updatedLayoutStyles.wrapper}>
      <style dangerouslySetInnerHTML={{ __html: loadingAnimationStyles }} />
      <Sidebar
        theme={theme}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        isMobile={isMobile}
        themeColors={themeColors}
        handleNewChat={handleNewChat}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isLoadingConversations={isLoadingConversations}
        conversationPage={conversationPage}
        filteredConversations={filteredConversations}
        isFetchingMessages={isFetchingMessages}
        currentConversationId={currentConversationId}
        hoveredConversationId={hoveredConversationId}
        setHoveredConversationId={setHoveredConversationId}
        handleConversationSelect={handleConversationSelect}
        editingTitleId={editingTitleId}
        editingTitle={editingTitle}
        setEditingTitle={setEditingTitle}
        handleTitleUpdate={handleTitleUpdate}
        setEditingTitleId={setEditingTitleId}
        startEditing={startEditing}
        deleteConversation={deleteConversation}
        hasMoreConversations={hasMoreConversations}
        userInfo={userInfo}
        setTheme={setTheme}
        unseenDoneIds={unseenDoneIds}
        sharedConversations={sharedConversations}
        projects={projects}
        // One chat, one home: the sidebar regroups the personal list itself
        // (own project chats leave the date groups and nest under their
        // project), so it needs the UNFILTERED list as well - a search that
        // matches a PROJECT NAME must still be able to reveal the chats inside
        // it, which the title-filtered copy has already thrown away.
        allConversations={conversations}
        hasTeam={hasTeam && sharedLoaded && projectsLoaded}
        // Nav rework: the PROJECT section lives above the date groups, expands
        // in place, and creates inline. The shell owns the expansion state and
        // the single create path.
        expandedProjects={expandedProjects}
        onToggleProject={handleToggleProject}
        onCreateProject={handleCreateProject}
        onNewChatInProject={handleNewChatInProject}
        canScopeProjects={hasTeam}
        activeProjectId={panelProjectId}
        teamName={userInfo?.team?.name || null}
        onLogout={handleLogout}
        surface={surface}
      />
       <MainContent
        theme={theme}
        setTheme={setTheme}
        selectedModels={selectedModels}
        setSelectedModels={setSelectedModels}
        isMobile={isMobile}
        fetchConversationMessages={fetchConversationMessages}
        themeColors={themeColors}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        userInfo={userInfo}  // Make sure this is passed
        isProfileOpen={isProfileOpen}
        setIsProfileOpen={setIsProfileOpen}
        dropdownRef={dropdownRef}
        isLoadingConversations={isLoadingConversations}
        messages={messages}
        isFirstMessageLoading={isFirstMessageLoading}
        isLoadingMessages={isLoadingMessages}
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
        chatContainerRef={chatContainerRef}
        messagesEndRef={messagesEndRef}
        paginationData={paginationData}
        currentConversationId={currentConversationId}
        isSwitchingConversation={isSwitchingConversation}
        messageStyles={messageStyles}
        renderMessage={renderMessage}
        UserIcon={UserIcon}
        AIIcon={AIIcon}
        updatedLayoutStyles={updatedLayoutStyles}
        isDragging={isDragging}
        isPerplexityModel={isPerplexityModel}
        shouldShowBrowserIcon={shouldShowBrowserIcon}
        textareaRef={textareaRef}
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        handleTextAreaHeight={handleTextAreaHeight}
        handleKeyPress={handleKeyPress}
        getPlaceholderText={getPlaceholderText}
        isLoading={isLoading}
        handleSubmit={handleSubmit}
        selectedFileName={selectedFileName}
        selectedFile={selectedFile}
        selectedFiles={selectedFiles}
        handleFileSelect={handleFileSelect}
        setSelectedFile={setSelectedFile}
        setSelectedFileName={setSelectedFileName}
        setSelectedFiles={setSelectedFiles}
        isFileUploadEnabled={isFileUploadEnabled}
        handleNewChat={handleNewChat}
        isLoadingConversation={isLoadingConversation} // Add this prop
        conversations={conversations}
        showRightPanel={panelDocked}
        handleClipboardPaste={handleClipboardPaste}
        uploadError={uploadError}
        onDismissUploadError={() => setUploadError(null)}
        onOpenVoice={voiceAllowed ? openVoiceMode : undefined}
        onCreditRedeemed={handleCreditRedeemed}
        readOnly={readOnly}
        conversationUuid={currentConversationUuid}
        sharedOwnerEmail={sharedOwnerEmail}
        onDuplicated={() => { fetchConversations(1); refreshSharedConversationsRef.current?.(); }}
        conversationTitle={conversationTitle}
        branchedFrom={branchedFrom}
        canShare={hasTeam && currentConversationId !== null && !readOnly}
        canLinkShare={!!currentConversationUuid && !readOnly}
        linkShareActive={linkShareActive}
        onOpenLinkShare={() => setLinkShareOpen(true)}
        shareVisibility={shareVisibility}
        sharePending={sharePending}
        shareReady={conversationMetaLoaded}
        onToggleShare={handleToggleShare}
        // NOT gated on team membership any more: personal projects mean a
        // team-less account has projects to file a chat into as well.
        canAssignProject={currentConversationId !== null && !readOnly}
        projects={projects}
        currentProjectId={conversationProjectId}
        projectPending={projectPending}
        onSelectProject={handleSelectProject}
        projectNeedsShareConfirm={projectNeedsShareConfirm}
        teamWelcome={teamWelcome}
        onDismissTeamWelcome={handleDismissTeamWelcome}
        hasTeam={hasTeam && sharedLoaded && projectsLoaded}
        playbooks={playbooks}
        onStartPlaybook={handleStartPlaybook}
        myPrograms={myPrograms}
        playbookRun={playbookRun}
        onCompletePlaybook={handleCompletePlaybook}
        playbookCompletePending={playbookCompletePending}
        // --- Nav rework: one shell, three surfaces ---
        surface={surface}
        // Passed alongside `panel` on purpose: the memo comparator cannot
        // compare a JSX node, so this scalar is what tells a heavy thread that
        // /p/1 became /p/2.
        panelProjectId={panelProjectId}
        onBackToChat={handleBackToChat}
        backLabel={backLabel}
        panelTitle={
          surface === 'team'
            ? (location.pathname.startsWith('/team/adoption')
              ? 'Adopsi'
              : location.pathname.startsWith('/team/people')
                ? 'Grup'
                : location.pathname.startsWith('/team/playbooks')
                  ? 'Playbook'
                  : location.pathname.startsWith('/team/programs')
                    ? 'Program adopsi'
                    : location.pathname.startsWith('/team/champion')
                      ? 'Champion AI'
                    : 'Tim')
            : surface === 'project'
              ? (projects.find((p) => p.id === panelProjectId)?.name || 'Project')
              : null
        }
        pendingProjectId={pendingProjectId}
        onClearPendingProject={handleClearPendingProject}
        onCreateProject={handleCreateProject}
        panel={
          surface === 'team' ? (
            <TeamPanel theme={theme} onDataChanged={handlePanelDataChanged} />
          ) : surface === 'project' && panelProjectId != null ? (
            <ProjectPanel
              key={panelProjectId}
              theme={theme}
              projectId={panelProjectId}
              onDataChanged={handlePanelDataChanged}
            />
          ) : null
        }
      />
      {/* Workspace panel is an owner-only surface: on a shared read-only
          conversation the /files calls answer 403 and it has nothing to offer
          but an error, so it is not mounted at all (showRightPanel already
          folds in !readOnly). */}
      {showRightPanel && (
        <RightSidebar
          theme={theme}
          themeColors={themeColors}
          currentConversationId={currentConversationId}
          projectId={currentConversationId != null ? conversationProjectId : pendingProjectId}
          messages={messages}
          overlay={isNarrow}
        />
      )}
      
      {/* Add popup outside of message components */}
      {popupPosition && openModelSwitchId && (
        <div 
          ref={modelSwitchRef}
          style={{
            position: 'fixed',
            top: popupPosition.top,
            left: popupPosition.left,
            backgroundColor: theme === 'light' ? '#FFFFFF' : '#2D2D2D',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            padding: '16px',
            width: '280px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            // Ensure the popup stays within viewport
            transform: `translate(${
              popupPosition.left + 280 > window.innerWidth ? '-100%' : '0'
            }, ${
              popupPosition.top + 500 > window.innerHeight ? '-50%' : '0'
            })`
          }}
        >
          <div style={{ marginBottom: '16px', flexShrink: 0 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#999' }}>Switch model</h3>
          </div>
          <div className="model-list-scroll" style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            marginRight: '-8px',
            paddingRight: '8px'
          }}>
            {modelCategories.map((category) => (
              <div key={category.category}>
                <div style={{
                  padding: '8px 12px',
                  color: theme === 'light' ? '#666' : '#888',
                  fontSize: '14px',
                  fontWeight: 500,
                  backgroundColor: theme === 'light' ? '#F5F5F5' : '#252525'
                }}>
                  {category.category}
                </div>
                {category.models.map((model) => {
                  // Get the message's model from the clicked message
                  const clickedMessage = messages.find(msg => msg.created_at === openModelSwitchId);
                  const messageModel = clickedMessage?.model || selectedModel;

                  // Check if model should be disabled
                  const isDisabled = isModelPremium(model.value) && userInfo?.status !== 'active';

                  return (
                    <div key={model.value} style={{
                      padding: '8px 12px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.5 : 1,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      background: model.value === messageModel
                        ? theme === 'light'
                          ? 'rgba(16, 163, 127, 0.1)'
                          : 'rgba(16, 163, 127, 0.2)'
                        : 'transparent',
                      borderLeft: model.value === messageModel
                        ? '3px solid #10A37F'
                        : '3px solid transparent',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => {
                      if (!isDisabled) {
                        e.currentTarget.style.background = model.value === messageModel
                          ? theme === 'light'
                            ? 'rgba(16, 163, 127, 0.15)'
                            : 'rgba(16, 163, 127, 0.25)'
                          : theme === 'light'
                            ? '#F5F5F5'
                            : '#252525';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isDisabled) {
                        e.currentTarget.style.background = model.value === messageModel
                          ? theme === 'light'
                            ? 'rgba(16, 163, 127, 0.1)'
                            : 'rgba(16, 163, 127, 0.2)'
                          : 'transparent';
                      }
                    }}
                    onClick={() => {
                      const newModel = model.value as Model;
                      
                      if (isDisabled) {
                        const upgradeMessage = "Free models are only available for PRO users. Please upgrade your account to use this model.";
                        alert(upgradeMessage);
                        return;
                      }

                      const clickedMessageIndex = messages.findIndex(msg => msg.created_at === openModelSwitchId);
                      let messageText = '';

                      if (clickedMessageIndex > 0) {
                        for (let i = clickedMessageIndex - 1; i >= 0; i--) {
                          if (messages[i].role === 'user') {
                            messageText = messages[i].content;
                            if (messageText.includes('[File:')) {
                              messageText = messageText.split('[File:')[0].trim();
                            }
                            break;
                          }
                        }
                      }

                      localStorage.setItem('lastSelectedModel', newModel);
                      setSelectedModel(newModel);
                      setPopupPosition(null);
                      setOpenModelSwitchId(null);
                      
                      handleSubmit(
                        { preventDefault: () => {} } as React.FormEvent, 
                        newModel,
                        messageText
                      );
                    }}
                    >
                      <img 
                        src={getModelIcon(model.value, theme)} 
                        alt={model.label} 
                        style={{ width: '20px', height: '20px', objectFit: 'contain' }} 
                      />
                      <div style={{ flex: 1, fontWeight: model.value === messageModel ? '500' : 'normal', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {model.label}
                        {model.isThinking && (
                          <span 
                            title="Thinking/Reasoning Model"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '18px',
                              height: '18px',
                              borderRadius: '4px',
                              backgroundColor: theme === 'light' ? '#F3E8FF' : '#581C87',
                              fontSize: '11px',
                              flexShrink: 0
                            }}
                          >
                            🧠
                          </span>
                        )}
                        {model.isVision && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme === 'light' ? '#666' : '#888'} strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        )}
                      </div>
                      {model.value === messageModel && (
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#10A37F" 
                          strokeWidth="2"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add styles for custom scrollbar */}
      <style>
        {`
          .model-list-scroll::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .model-list-scroll::-webkit-scrollbar-track {
            background: ${theme === 'light' ? '#f1f1f1' : '#1a1a1a'};
            border-radius: 4px;
          }
          .model-list-scroll::-webkit-scrollbar-thumb {
            background: ${theme === 'light' ? '#c1c1c1' : '#404040'};
            border-radius: 4px;
          }
          .model-list-scroll::-webkit-scrollbar-thumb:hover {
            background: ${theme === 'light' ? '#a1a1a1' : '#4a4a4a'};
          }
          .model-list-scroll {
            scrollbar-width: thin;
            scrollbar-color: ${theme === 'light' ? '#c1c1c1' : '#404040'} ${theme === 'light' ? '#f1f1f1' : '#1a1a1a'};
          }
        `}
      </style>

      {/* Surface notice: a small dismissible strip for things that happened on
          the way here (e.g. /p/999 for a project that is gone or not ours, which
          used to drop the user on /c with no explanation at all). */}
      {surfaceNotice && (
        <div
          role={surfaceNotice.tone === 'error' ? 'alert' : 'status'}
          style={{
            position: 'fixed',
            top: isMobile ? '66px' : '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            maxWidth: 'min(420px, calc(100vw - 32px))',
            padding: '8px 8px 8px 12px',
            borderRadius: '12px',
            background: theme === 'dark' ? 'rgba(20,22,25,0.96)' : 'rgba(255,255,255,0.97)',
            border: `1px solid ${
              surfaceNotice.tone === 'error'
                ? (theme === 'light' ? 'rgba(220,38,38,0.35)' : 'rgba(248,113,113,0.35)')
                : currentThemeColors.line2
            }`,
            boxShadow: currentThemeColors.shadow,
            fontSize: '12.5px',
            color:
              surfaceNotice.tone === 'error'
                ? (theme === 'light' ? '#DC2626' : '#F87171')
                : currentThemeColors.ink,
          }}
        >
          <span
            title={surfaceNotice.text}
            style={{
              minWidth: 0,
              // Errors carry a backend `detail` that can run past one line, so
              // the strip wraps (capped) instead of ellipsing the useful half
              // of the sentence away.
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              lineHeight: 1.45,
            }}
          >
            {surfaceNotice.text}
          </span>
          <button
            type="button"
            onClick={() => setSurfaceNotice(null)}
            title="Tutup"
            aria-label="Tutup pemberitahuan"
            className="pa-icon-ghost"
            style={{
              display: 'grid',
              placeItems: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: 'none',
              border: 'none',
              color: currentThemeColors.ink3,
              cursor: 'pointer',
              flexShrink: 0,
              padding: 0,
              ['--pa-focus-ring' as any]: currentThemeColors.jade,
              ['--pa-focus-ring-dim' as any]: currentThemeColors.jadeDim,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* 3) Add the scroll-to-bottom button (chat surface only: the panels own
             their own scroller and have nothing to scroll to, and an EMPTY
             thread has nothing below the fold either - it used to float over
             the welcome screen). */}
      {showScrollToBottom && isChatSurface && messages.length > 0 && (
        <button
          onClick={() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            setShouldScroll(true);
            setShowScrollToBottom(false);
          }}
          style={{
            position: 'fixed',
            // Mobile composer stack measures ~150px tall + home indicator;
            // 120px parked the button ON the composer (2026-08-03 review).
            bottom: isMobile
              ? 'calc(168px + env(safe-area-inset-bottom))'
              : '120px',
            right: panelDocked ? '344px' : '24px',
            zIndex: 1000,
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: theme === 'light'
              ? 'rgba(255, 255, 255, 0.9)'
              : 'rgba(26, 26, 29, 0.9)',
            color: currentThemeColors.secondaryText,
            cursor: 'pointer',
            boxShadow: currentThemeColors.shadow,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            border: `1px solid ${currentThemeColors.border}`,
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.color = currentThemeColors.text;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.color = currentThemeColors.secondaryText;
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="m19 12-7 7-7-7" />
          </svg>
        </button>
      )}
      
      {/* No composer on a shared conversation the viewer does not own, and none
          at all on the team/project surfaces (there is no thread to write to). */}
      {messages.length > 0 && !readOnly && isChatSurface && (
        <ChatInput
          theme={theme}
          themeColors={themeColors}
          isDragging={isDragging}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          isPerplexityModel={isPerplexityModel}
          shouldShowBrowserIcon={shouldShowBrowserIcon}
          selectedModel={selectedModel}
          textareaRef={textareaRef}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          handleTextAreaHeight={handleTextAreaHeight}
          handleKeyPress={handleKeyPress}
          getPlaceholderText={getPlaceholderText}
          isLoading={isLoading}
          handleSubmit={handleSubmit}
          selectedFileName={selectedFileName}
          selectedFile={selectedFile}
          selectedFiles={selectedFiles}
          handleFileSelect={handleFileSelect}
          setSelectedFile={setSelectedFile}
          setSelectedFileName={setSelectedFileName}
          setSelectedFiles={setSelectedFiles}
          isFileUploadEnabled={isFileUploadEnabled}
          showRightPanel={panelDocked}
          showScrollToBottom={showScrollToBottom}
          onScrollToBottom={() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            setShouldScroll(true);
            setShowScrollToBottom(false);
          }}
          isSidebarOpen={isSidebarOpen}
          // Keep the composer clear of the collapsed rail (desktop only).
          railOffset={isMobile ? 0 : 52}
          handleClipboardPaste={handleClipboardPaste}
          selectedModels={selectedModels}
          setSelectedModels={setSelectedModels}
          uploadError={uploadError}
          onDismissUploadError={() => setUploadError(null)}
          conversationId={currentConversationId}
          projectId={currentConversationId != null ? conversationProjectId : pendingProjectId}
          onOpenVoice={voiceAllowed ? openVoiceMode : undefined}
        />
      )}

      {voiceConv && (
        <VoiceMode
          conversationId={voiceConv.id}
          theme={theme}
          onClose={closeVoiceMode}
          onComicDone={(comic) => revealVoiceComic(voiceConv, comic)}
        />
      )}

      {currentConversationUuid && (
        <ShareLinkModal
          open={linkShareOpen}
          onClose={() => setLinkShareOpen(false)}
          conversationUuid={currentConversationUuid}
          theme={theme}
          c={themeColors[theme]}
          onChanged={setLinkShareActive}
        />
      )}

      {/* In-app lightbox for markdown images (singleton, portal to body) */}
      <ImageLightbox theme={theme} />

    </div>
  );

};


export default Chat;
